import { SelectionModel } from '@angular/cdk/collections';
import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { CdkVirtualScrollable, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  Injector,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  signal,
  Signal,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { produce } from 'immer';
import uniqBy from 'lodash-es/uniqBy';
import {
  animationFrameScheduler,
  auditTime,
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  merge,
  Observable,
  pairwise,
  retry,
  skip,
  Subject,
  Subscription,
  take,
  takeUntil,
  tap,
  timer,
} from 'rxjs';

import { runtimeActions, runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  PropsOf,
  RuntimeEventQueryData,
  TagConfig,
  UpdatePropRequest,
} from '@blaze-runtime/engine-domain';
import {
  BLAZE_SCROLL_OFFSET_ADAPTER,
  BlazeVirtualScrollableElementDirective,
  COMPLEX_FILTER_SERVICE,
  HOST_ELEMENT,
  LogicalQueryFormGroupValue,
  RowAutosizeVirtualScrollDirective,
  VIRTUAL_SCROLL_ITEM_SIZE_PREDICTOR,
  VIRTUAL_SCROLL_NODES_PROVIDER,
  VirtualScrollableRegistryService,
  WindowScrollOffsetAdapter,
} from '@blaze-runtime/engine-ui-feature';
import { markEventAsHandled } from '@blaze-runtime/engine-util';
import {
  ColumnId,
  DatasetFilterInfo,
  DatasetFilterScope,
  LogicalQueryNode,
} from '@blaze-shared/api-contracts-domain';
import { generalSettingsSelectors } from '@blaze-shared/app-state-data-access';
import { AuthService } from '@blaze-shared/auth-data-access';
import { DatasetsApiService } from '@blaze-shared/datasets-data-access';
import {
  createSortingDataAccessor,
  GROUP_BY_KEY,
  mapLogicalQueryNodeToLogicalPredicateNode,
} from '@blaze-shared/datasets-domain';
import {
  ActionsDisplayMode,
  type BorderRadiusValue,
  ContainerActionConfig,
  ContainerElementMetaUiFeatures,
  ContainerFieldElementMetaUiFeatures,
  DataBindingSourceInfo,
  DEFAULT_ELEMENT_PADDING,
  DEFAULT_NO_DATA_MESSAGE,
  ElementQueryState,
  ElementRedirect,
  GenericSize,
  getActiveBinding,
  ITEMS_CONTAINER_ACTION_ADD,
  ITEMS_CONTAINER_ACTION_LINK_CLICK,
  ITEMS_CONTAINER_ACTION_ROW_CLICK,
  makeBorderStyles,
  makeContainerVisualEffectStyles,
  makeFontStyles,
  makeFontStyleStyles,
  PaginatedComponentModel,
  PaginationDirection,
  PredefinedRowAction,
  PropertiesConfigInputContainerItemActionModel,
  PropertiesConfigInputTableColumnModel,
  PropertiesConfigInputTableConditionalFormatModel,
  ResizeBehavior,
  SearchAndFiltersMode,
  TABLE_ACTIONS_COLUMN_NAME,
  TABLE_SELECT_COLUMN_NAME,
  TableColumnFormat,
  TableConditionalFormatColumnSelection,
  tableConditionalFormatIconStyle,
  tableConditionalFormatStyle,
  TableRowClickId,
} from '@blaze-shared/elements-domain';
import { IntelliSenseService } from '@blaze-shared/intellisense-feature';
import { NotificationService } from '@blaze-shared/platform-data-access';
import { PagesApiService } from '@blaze-shared/projects-data-access';
import { BlazeTableRecord } from '@blaze-shared/schema-data-access';
import {
  BlazeSchema,
  convertBlazeSchemaToElementSchema,
  DatasetRow,
  findColumn,
  getColumnKey,
  GroupByRow,
  isGroupByRow,
  isWorkflowSchema,
} from '@blaze-shared/schema-domain';
import {
  DialogService,
  formatGeoPoint,
  isDurationEq,
  isGeoPoint,
} from '@blaze-shared/shared-feature';
import { mapOfTemplates, PaginatorComponent, safeDetectChanges } from '@blaze-shared/shared-ui';
import {
  countedShareReplay,
  deepClone,
  delayUntil,
  hasIcons,
  intersectionRxUtils,
  isDeepEqual,
  isNullish,
  resizeRxUtils,
  runAfterFramePaint,
  setAlphaToHexColor,
  useEmitTracker,
  valueToBool,
} from '@blaze-shared/shared-util';
import { CssMediaService } from '@blaze-shared/theming-data-access';
import { FALLBACK_THEME } from '@blaze-shared/theming-domain';
import { FileResult } from '@blaze-shared/uploader';
import { isGetNode, setValueByPath } from '@blaze-shared/workflow-domain';

import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { FilterBuilderComplexFilterService } from '../../components/complex-filters/filter-builder/filter-builder-complex-filter.service';
import { FilterBuilderComponent } from '../../components/complex-filters/filter-builder/filter-builder.component';
import { matTooltipDefaultsProvider } from '../../providers/mat-tooltip-defaults.provider';
import { ListLikeActionArgs, ListLikeActionResult } from '../../services/container-actions.service';
import {
  ElementPaginationService,
  PAGINATION_COUNT_OPTIONS,
} from '../../services/element-pagination.service';
import { RuntimeService } from '../../services/runtime.service';
import { makeCssStringFromObj } from '../../services/styles';
import { TableInlineEditorUtilsService } from '../../services/table-inline-editor-utils.service';
import { determineIdColumnNameOfDatasource } from '../../utils/canvas-element/bindings.utils';
import { shouldShowEmptyDataMessage } from '../../utils/elements/element.utils';
import {
  destroyOverlayRef,
  generatePositionStrategyForOverlay,
  getEditMode,
  isCellUpdateFromTag,
  isColumnValueInlineEditable,
  isDateTimeEq,
  MenuEditModeColumnFormats,
  MenuEditModeDataTypes,
  scrollCellIntoViewIfNeeded,
} from '../../utils/elements/table-inline-editor.utils';
import { CanvasElementBaseWithActionsComponent } from '../__base/base-with-actions.component';
import {
  ElementDataActions,
  getDataSourceIdColumn,
  OnContainerActionParams,
  PropertiesConfigInputTableColumnModelForGrouping,
} from '../__base/element-data-actions';

import {
  EVALUATE_EXPRESSION_FN,
  EvaluateExpressionFn,
  NORMALIZE_LINKED_PARAMS_FN,
  NormalizeLinkedParamsFn,
  TableColumnsVisibilityService,
} from './column-visibility/table-columns-visibility.service';
import { computeColumnExtremes } from './compute-column-extremes';
import { TableBaseEditorComponent } from './inline-editor/base-editor/base-editor.component';
import {
  CellEditError,
  CellEditErrors,
  CellValueUpdateFromLink,
  CellValueUpdateFromTag,
  CellValueUpdateFromTimeZone,
  CellValueUpdateModel,
  CellValueUpdateModelForFileAction,
  CellValueUpdateModelForImageAction,
  ColumnEditableConfig,
  UpdateInlineRowValue,
} from './inline-editor/base-editor/inline-editor.types';
import { isAllSelected } from './pipes/is-all-selected.pipe';
import { TableItemHeightPredictor } from './providers/table-item-height-predictor';
import { TableLinkCellActionService } from './providers/table-link-cell-action.service';
import { TableRowsProvider } from './providers/table-rows-provider';
import { TableSourceWorkflowOutcomeService } from './providers/table-source-workflow-outcome.service';
import { TableBodyComponent } from './table-body/table-body.component';
import { type UITableCellStyling } from './table-cell-styling';
import { CellEditEvent, TableRowComponent } from './table-row/table-row.component';
import { TableSortService } from './table-sort.service';
import { TableCellExpectedDownError, TablePropsModel } from './table.props-model';
import {
  ACTIONS_COLUMN_DEFAULT_WIDTH,
  ACTIONS_COLUMN_NAME,
  COLUMN_DEFAULT_WIDTH,
  SELECT_COLUMN_DEFAULT_WIDTH,
  SELECT_COLUMN_NAME,
  TableVirtualScrollFacade,
} from './virtual-scroll';

const sortMap: Record<string, 'Ascending' | 'Descending'> = {
  asc: 'Ascending',
  desc: 'Descending',
};

interface SortValues {
  field: ColumnId | string;
  direction: 'asc' | 'desc' | '';
}

/**
 * Lines of files are taller than text lines.
 * This mapping shows how many lines of files can fit when table's line clamp is set to certain value.
 */
const TABLE_FILE_LINE_CLAMP_MAPPING: Record<number, number> = {
  1: 1,
  2: 1,
  3: 2,
  4: 2,
  5: 2,
  6: 3,
  7: 3,
  8: 4,
  9: 4,
  10: 5,
};

const rowSizeMap: Record<GenericSize, { padding: number; rowHeight: number }> = {
  small: {
    padding: 12,
    rowHeight: 40,
  },
  medium: {
    padding: 16,
    rowHeight: 48,
  },
  large: {
    padding: 28,
    rowHeight: 72,
  },
};

const DEFAULT_LINE_HEIGHT_PX = 16;
const DEFAULT_ROW_PADDING_PX = 12;

const COLUMN_MIN_BUFFER_PX = 200;
const COLUMN_MAX_BUFFER_PX = 500;

interface CellEditInfo {
  event: MouseEvent | FocusEvent;
  columnId: string | undefined;
  columnName: string;
  rowIndex: number;
  cell: HTMLElement;
}

@UntilDestroy()
@Component({
  selector: 'be-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    matTooltipDefaultsProvider,
    VirtualScrollableRegistryService,
    FilterBuilderComplexFilterService,
    TableColumnsVisibilityService,
    {
      provide: COMPLEX_FILTER_SERVICE,
      useExisting: FilterBuilderComplexFilterService,
    },
    {
      provide: HOST_ELEMENT,
      useFactory: (ref: ElementRef<HTMLElement>): HTMLElement => ref.nativeElement,
      deps: [ElementRef<HTMLElement>],
    },
    {
      provide: EVALUATE_EXPRESSION_FN,
      useFactory: (service: IntelliSenseService): EvaluateExpressionFn =>
        service.evalExpressionObj.bind(service),
      deps: [IntelliSenseService],
    },
    {
      provide: NORMALIZE_LINKED_PARAMS_FN,
      useFactory: (service: IntelliSenseService): NormalizeLinkedParamsFn =>
        service.normalizeLinkedParams.bind(service),
      deps: [IntelliSenseService],
    },
    {
      provide: ACTIONS_COLUMN_NAME,
      useValue: TABLE_ACTIONS_COLUMN_NAME,
    },
    {
      provide: SELECT_COLUMN_NAME,
      useValue: TABLE_SELECT_COLUMN_NAME,
    },
    {
      provide: COLUMN_DEFAULT_WIDTH,
      useValue: 120,
    },
    {
      provide: SELECT_COLUMN_DEFAULT_WIDTH,
      useValue: 30,
    },
    {
      provide: ACTIONS_COLUMN_DEFAULT_WIDTH,
      useValue: 76,
    },
    {
      provide: VIRTUAL_SCROLL_ITEM_SIZE_PREDICTOR,
      useClass: TableItemHeightPredictor,
    },
    {
      provide: VIRTUAL_SCROLL_NODES_PROVIDER,
      useClass: TableRowsProvider,
    },
    {
      provide: BLAZE_SCROLL_OFFSET_ADAPTER,
      useClass: WindowScrollOffsetAdapter,
    },
    ElementDataActions<'table'>,
    TableLinkCellActionService,
    TableSortService,
    TableSourceWorkflowOutcomeService,
    TableVirtualScrollFacade,
  ],
  standalone: false,
})
export class TableComponent
  extends CanvasElementBaseWithActionsComponent<'table'>
  implements CanvasElementComponent<'table'>, OnChanges, OnInit, AfterViewInit, OnDestroy
{
  private readonly authService = inject(AuthService);
  private readonly complexFilterService = inject(FilterBuilderComplexFilterService);
  private readonly datasetsApiService = inject(DatasetsApiService);
  private readonly dialogService = inject(DialogService);
  private readonly elementPaginationService = inject(ElementPaginationService);
  private readonly hostEl = inject(ElementRef<HTMLElement>);
  private readonly ngZone = inject(NgZone);
  private readonly notificationService = inject(NotificationService);
  private readonly overlay = inject(Overlay);
  private readonly pagesApi = inject(PagesApiService);
  private readonly runtimeService = inject(RuntimeService);
  private readonly scrollableRegistry = inject(VirtualScrollableRegistryService);
  private readonly sortService = inject(TableSortService);
  private readonly sourceWorkflowOutcomeService = inject(TableSourceWorkflowOutcomeService);
  private readonly tableColumnsVisibilityService = inject(TableColumnsVisibilityService);
  private readonly tableInlineEditorUtilsService = inject(TableInlineEditorUtilsService);
  private readonly tableLinkCellActionService = inject(TableLinkCellActionService);
  private readonly tableVirtualScrollFacade = inject(TableVirtualScrollFacade);
  private readonly intelliSenseService = inject(IntelliSenseService);
  private readonly cssMediaService = inject(CssMediaService);
  private readonly injector = inject(Injector);

  protected readonly elementDataActions = inject(ElementDataActions);

  @Input() queryState$?: Observable<ElementQueryState | undefined>;
  @Input() paginationPending$?: Observable<boolean>;

  @Output() updateProp = new EventEmitter<UpdatePropRequest>();

  @ViewChildren(TemplateRef) tpls!: QueryList<TemplateRef<unknown>>;
  @ViewChildren('tableCell') tableCells!: QueryList<HTMLTableCellElement>;
  @ViewChildren('row') tableRows: QueryList<TableRowComponent<any>> | null = null;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(PaginatorComponent) paginator!: PaginatorComponent;
  @ViewChild('loadMore') loadMore!: ElementRef<HTMLDivElement>;
  @ViewChild(BlazeVirtualScrollableElementDirective) scrollable: CdkVirtualScrollable | null = null;
  @ViewChild('tableWrapper') tableWrapper: ElementRef<HTMLElement>;
  @ViewChild('table', { static: true }) tableHtmlElement!: ElementRef<HTMLDivElement>;
  @ViewChild(CdkVirtualScrollViewport) cdkViewport!: CdkVirtualScrollViewport;
  @ViewChild('filterBuilder') filterBuilderComponent: FilterBuilderComponent | null = null;
  @ViewChild('blazeTableBody') blazeTableBody: TableBodyComponent | null = null;
  @HostBinding('style.--b-t-accent') accent = FALLBACK_THEME.value.accent;
  @HostBinding('style.--b-t-container-radius') containerRadius = '0';
  @HostBinding('style.--b-t-container-bg') containerBg = 'transparent';
  @HostBinding('style.--b-t-border-color') borderColor = 'transparent';
  @HostBinding('style.--b-t-border-color-header') borderColorHeader = 'transparent';
  @HostBinding('style.--b-t-border-color-outline') borderColorOutline = 'transparent';
  @HostBinding('style.--b-t-border-color-row') borderColorRow = 'transparent';
  @HostBinding('style.--b-t-border-color-cell') borderColorCell = 'transparent';
  @HostBinding('style.--b-t-border-radius') borderRadius: BorderRadiusValue = '0px';
  @HostBinding('style.--b-t-internal-padding') internalPadding? = DEFAULT_ELEMENT_PADDING;
  @HostBinding('style.--b-t-font-size') fontSize = FALLBACK_THEME.value.fontSize;
  @HostBinding('style.--b-t-font-family') fontFamily = FALLBACK_THEME.value.fontFamily;
  @HostBinding('style.--b-t-line-height') lineHeight = FALLBACK_THEME.value.lineHeight;
  @HostBinding('style.--b-t-header-color') headerColor = '#000';
  @HostBinding('style.--b-t-header-bg') headerBackground = 'transparent';
  @HostBinding('style.--b-t-cell-color') cellColor = '#000';
  @HostBinding('style.--b-t-cell-bg') cellBackground = 'transparent';
  @HostBinding('style.--b-t-cell-hover-bg') cellHoverBackground = 'transparent';
  @HostBinding('style.--b-t-line-clamp') lineClamp = 1;
  @HostBinding('style.--b-t-file-line-clamp') fileLineClamp: number | 'none' = 'none';
  @HostBinding('style.--b-t-light-cell-color') lightCellColor = '#606b7cA0';
  @HostBinding('style.--b-t-lighter-cell-color') lighterCellColor = '#939ba5';
  @HostBinding('style.--b-t-lightest-cell-color') lightestCellColor = '#c9cdd2';
  @HostBinding('style.--b-col-bool-on-bg') colBoolOnBg = FALLBACK_THEME.value.accent;
  @HostBinding('style.--b-col-bool-on-mark') colBoolOnMark = FALLBACK_THEME.value.fill;
  @HostBinding('style.--b-col-bool-off-bg') colBoolOffBg = '#37373780';
  @HostBinding('style.--b-col-bool-off-mark') colBoolOffMark = FALLBACK_THEME.value.fill;

  private readonly outputEmitTracker = useEmitTracker();
  private readonly paginationEmitTracker = useEmitTracker();

  private readonly dataSource$$ = new BehaviorSubject<DataBindingSourceInfo | undefined>(undefined);

  private isAddingCheckInitialized: boolean | 'primitive' = false;
  private paginatorTimeout?: ReturnType<typeof setTimeout>;

  private complexFiltersInitialized = false;
  private selectionSubscription?: Subscription;

  private readonly cellEdit$$ = new BehaviorSubject<CellValueUpdateModel | undefined>(undefined);
  private readonly cellMouseDown$$ = new BehaviorSubject<boolean>(false);
  private readonly cellMouseNotDown$ = this.cellMouseDown$$.pipe(filter((value) => !value));
  private editableElementsForTab?: { next?: CellValueUpdateModel; prev?: CellValueUpdateModel };
  private readonly dataSourceConnectedData$$: BehaviorSubject<typeof this.dataSource.data>;
  private readonly componentDestroy$ = new Subject();
  private projectId: string | undefined;
  private readonly sortDataFnDefault;
  private readonly sortDataFnGroupBy;

  private cellSelectedRowBackground = '';
  private isInInlineEditModeSub?: Subscription;
  private assignRowActiveRowClickActionSub?: Subscription;

  private readonly isAnyRowActionVisible = signal(false);
  private readonly invisibleColumns = signal<string[]>([]);
  private readonly columns = signal<ContainerElementMetaUiFeatures['columns']>([]);

  private readonly createTagColorConfig$$ = new Subject<PropertiesConfigInputTableColumnModel[]>();

  /** input for table's dataSource */
  private _value!: any[];
  private readonly allActions$$ = new BehaviorSubject<
    PropertiesConfigInputContainerItemActionModel[]
  >([]);
  private _lastTableWrapperWidth = 0;

  protected readonly hasRowNumbering = signal(false);

  formatters$:
    | Observable<
        (
          item: any | null,
          columnKey: string,
        ) => PropertiesConfigInputTableConditionalFormatModel | undefined
      >
    | undefined;

  isPrintMode = computed(() => this.cssMediaService.isPrintMode() ?? false);

  tableWrapperRect?: { x: number; width: number; height: number };
  isHeaderFloating = false;
  isFooterFloating = false;

  readonly elementKeyRoot = 'b-t';
  readonly elementDropdownKeyRoot = 'b-td';
  readonly elementInlineEditKeyRoot = 'b-tie';
  readonly elementActionsKeyRoot = 'b-tac';
  elementKey!: string;
  elementDropdownKey!: string;
  elementInlineEditKey!: string;
  elementActionsKey!: string;

  actionsColumnName = TABLE_ACTIONS_COLUMN_NAME;
  selectColumnName = TABLE_SELECT_COLUMN_NAME;
  cellsMap: Record<TableColumnFormat, TemplateRef<unknown>> | null = null;

  wrappedGroupsIds: Set<string> = new Set([]);
  wrappedRecordsIds: Set<string> = new Set([]);

  labelEnabled!: boolean;
  labelText!: string;
  icon!: string;

  itemAdd?: PredefinedRowAction & { label: string };

  isAddingRowsDisabled = false;
  noDataMessage!: string;
  rowActions: PropertiesConfigInputContainerItemActionModel[] = [];
  rowActionsHaveIcons = false;
  rowActionsDisplayMode: ActionsDisplayMode = 'always';
  rowSize: GenericSize = 'medium';
  rowHeightBehavior: ResizeBehavior = 'fixed';
  rowActionsGrouped = false;

  bulkSelectionTriggerLabel?: string;
  bulkSelectionLabel?: string;

  dataSource = new MatTableDataSource<Record<string, any>>([]);
  /** Definitions updated with latest column names at runtime */
  displayedColumnsDef: PropertiesConfigInputTableColumnModel[] = [];
  originalColumnSizes: Record<string, number> = {};
  /** Runtime column names */
  displayedColumns: string[] = [];
  /* Should stay undefined before any data is loaded */
  dataRowsCount: number | undefined = undefined;
  sortValues: SortValues = { field: '', direction: '' };
  query?: LogicalQueryNode;
  paginationProps: PaginatedComponentModel = {
    state: {
      hasPreviousPage: false,
      hasNextPage: false,
    },
    countOptions: PAGINATION_COUNT_OPTIONS,
    isServerSide: true,
    enabled: false,
    pageIndex: 0,
    pageSize: PAGINATION_COUNT_OPTIONS[0],
    backwardPaginationSupported: true,
  };
  autoSelectFirstRow: boolean | undefined;
  groups?: (string | ColumnId)[];
  groupsStyling: {
    fontStyles?: Record<string, any>;
    groupColors?: string[];
  } = {};
  columnsForResize$: Observable<PropertiesConfigInputTableColumnModel[]>;

  cellsEditConfig: ColumnEditableConfig | undefined;
  linkCellValueClickConfig: Record<string, string | boolean> | undefined; // pageId's
  isMultipleValueAllowed: Record<string, boolean> | undefined;
  cellsEditErrors: CellEditErrors = {};
  isInInlineEditMode = false;

  /**
   * Used to prevent initial pagination request.
   */
  firstValueArrived = false;

  itemAddVisible$: Observable<boolean>;

  /**
   * Stores cached information about total data rows.
   * Server returns this value only on first page.
   */
  cachedTotalRowsCount = 0;
  totalRowsChanged = false;

  titleColor!: string;
  wrapperStyles: Record<string, string> | null = null;

  tagConfig: Record<string, TagConfig['0']> = {};

  displayedRowsLimit$!: Observable<number>;

  searchMode: SearchAndFiltersMode = SearchAndFiltersMode.PREDEFINED;
  isVirtualScrollRegistered = false;
  rowWidth = this.tableVirtualScrollFacade.rowWidth();

  savedFilters: DatasetFilterInfo[] = [];
  savedFiltersLoading = true;
  allowToSavePublicComplexFilters = false;
  runtimeModePublicOrSecure$ = this.store.select(
    generalSettingsSelectors.isRuntimeModePublicOrSecure,
  );

  actionOnRowClickId?: TableRowClickId;
  actionOnRowClick?: ElementRedirect | string;
  actionOnHoverIndex?: number | string;
  rowClickEnabled: Signal<boolean | undefined>;

  headerPositionOffset$!: Observable<number>;
  headerStartOffset$!: Observable<number>;
  bottombarPositionOffset$!: Observable<number>;
  bottombarEndOffset$!: Observable<number>;
  columnsExtremes = signal<Record<string, { min: number; max: number }>>({});

  readonly SearchAndFiltersMode = SearchAndFiltersMode;

  isCheckboxCellChecked = valueToBool;

  readonly trackById = (index: number, item: any): any => {
    return this.idColumnName ? item[this.idColumnName] : index;
  };

  @Input() set value(data: any[]) {
    this._value = this.addTempIdToData(data);
  }
  get value(): any[] {
    return this._value;
  }

  get lineHeightPx(): number {
    const lineHeightPx = +this.lineHeight.replace('px', '');
    return isNaN(lineHeightPx) ? DEFAULT_LINE_HEIGHT_PX : lineHeightPx;
  }

  get rowPaddingPx(): number {
    return (rowSizeMap[this.rowSize]?.padding ?? DEFAULT_ROW_PADDING_PX) * 2;
  }

  get rowHeightPx(): number {
    return this.rowPaddingPx + this.lineClamp * this.lineHeightPx;
  }

  get groupingRowHeightPx(): number {
    return (
      (rowSizeMap['small'].padding ?? DEFAULT_ROW_PADDING_PX) * 2 +
      this.lineClamp * this.lineHeightPx
    );
  }

  get columnMinBufferPx(): number {
    return COLUMN_MIN_BUFFER_PX;
  }

  get columnMaxBufferPx(): number {
    return COLUMN_MAX_BUFFER_PX;
  }

  get totalCount(): number | undefined {
    return this.cachedTotalRowsCount || this.dataRowsCount;
  }

  get cellStyles(): UITableCellStyling {
    return {
      cellColor: this.cellColor,
      cellBackground: this.cellBackground,
      cellHoverBackground: this.cellHoverBackground,
      cellSelectedRowBackground: this.cellSelectedRowBackground,
    };
  }

  get collapseGroupsByDefault(): boolean {
    return this.props?.content.table?.groupByStartWithCollapsed || false;
  }

  get selection(): SelectionModel<unknown> {
    return this.elementDataActions.selection;
  }
  get focus(): SelectionModel<unknown> {
    return this.elementDataActions.focus;
  }

  get shouldUseClientPagination(): boolean {
    return (
      !this.bindingResults?.['value'] ||
      !this.paginationProps.enabled ||
      !this.paginationProps.isServerSide
    );
  }

  get selectionCount(): number {
    return this.selection.selected.filter((data) => !isGroupByRow(data)).length;
  }

  get dataSourceOrderedData(): typeof this.dataSource.data {
    return this.dataSourceConnectedData$$.value;
  }

  get footerAlwaysVisible(): boolean {
    return this.props?.content?.table?.footerAlwaysVisible === true;
  }

  get headerAlwaysVisible(): boolean {
    return this.props?.content?.table?.headerAlwaysVisible === true;
  }

  get stickyFooterTriggerOn(): HTMLElement | null {
    return this.blazeTableBody?.tableBodyElement()?.nativeElement ?? null;
  }

  get footerScrollSync(): string[] {
    return [
      `.element-container-${this.elementKey} .blaze-virtual-scrollable`,
      `.${this.elementKey} .header-wrapper`,
    ];
  }

  get tableContainerScrollSync(): string[] {
    return [`.${this.elementKey} .header-wrapper`];
  }

  get virtualScrollStrategy(): RowAutosizeVirtualScrollDirective<any> | null {
    return this.blazeTableBody?.virtualScrollStrategy() ?? null;
  }

  constructor() {
    super();
    this.elementDataActions.handlesFocus = true;
    this.elementDataActions.props = toSignal(this.props$);
    this.elementDataActions.dataValue = this.dataValue;
    this.elementDataActions.elementId = toSignal(this.elementId$);
    this.elementDataActions.elementType = this.elementType;

    this.sortDataFnDefault = this.dataSource.sortData;
    this.sortDataFnGroupBy = this.sortService.sortGroupedTableRows.bind(this.sortService);

    this.initializeConditionalFormatters();

    this.createTagConfig(
      this.createTagColorConfig$$.pipe(
        map((columns) =>
          columns.map((col) => ({
            column: getColumnKey(col),
            tagsPalette: col.tagsPalette,
          })),
        ),
      ),
      this.props$.pipe(map((props) => props?.content.columns)),
      this.createPaletteSchemaObservable(this.dataSource$$),
      this.dataValue.data$,
    ).subscribe((tagConfig) => {
      this.tagConfig = Object.fromEntries(
        tagConfig.map((tag) => [tag?.id || tag?.name, tag]).filter((tag) => tag[0]),
      );
      this.cdr.detectChanges();
    });

    this.rowClickEnabled = toSignal(
      this.visibilityComponentScope((props) => ({
        enabled: props.content.rowClick?.enabled,
        expression: props.content.rowClick?.visibilityExpression,
      })),
    );

    this.itemAddVisible$ = combineLatest({
      vcs: this.visibilityComponentScope((props) => ({
        enabled: props.content.itemAdd.enabled,
        expression: props.content.itemAdd.visibilityExpression,
      })),
      userAccessRoles: this.userAccessRolesService.userAccessRoles$,
    }).pipe(
      map(({ vcs }) => {
        const accessRoles = this.props?.content.itemAdd?.visibilityConditions?.accessRoles;
        const isInAccessRoles =
          !accessRoles?.length ||
          this.userAccessRolesService.isCurrentUserInAccessRoles(accessRoles);
        return vcs && isInAccessRoles;
      }),
    );

    this.elementDataActions.visibleRowActions$ = this.elementDataActions.getVisibleActions(
      this.runtimeContext$,
      this.dataSource.connect(),
      this.props$.pipe(
        map((props) => props?.content.unifiedRowActions),
        distinctUntilChanged(isDeepEqual),
      ),
      this.element$,
    );

    this.elementDataActions.visibleBulkActions$ =
      this.elementDataActions.getVisibleComponentActions(
        this.runtimeContext$,
        this.elementId,
        this.props$.pipe(
          map((props) => props?.content.unifiedBulkActions),
          distinctUntilChanged(isDeepEqual),
        ),
      );

    this.elementDataActions.visibleOtherActions$ =
      this.elementDataActions.getVisibleComponentActions(
        this.runtimeContext$,
        this.elementId,
        this.props$.pipe(
          map((props) => props?.content.unifiedOtherActions),
          distinctUntilChanged(isDeepEqual),
        ),
      );

    this.elementDataActions.visibleHeaderActions$ = combineLatest({
      bulk: this.elementDataActions.visibleBulkActions$,
      other: this.elementDataActions.visibleOtherActions$,
    }).pipe(
      map(({ bulk, other }) => [
        ...(other ?? []).map((action) => ({ ...action, actionKind: 'other' as const })),
        ...(bulk ?? []).map((action) => ({ ...action, actionKind: 'bulk' as const })),
      ]),
    );

    this.elementDataActions
      .clearActionsNoLongerPending(this.element$)
      .pipe(untilDestroyed(this))
      .subscribe();

    this.initializeOffsetsObservables();

    this.initializeCellEditObservable();

    this.dataSourceConnectedData$$ = this.dataSource.connect();

    this.columnsForResize$ = this.props$.pipe(
      map((props) => {
        return (
          ([...(props?.content.columns || []), props?.content.table?.groupByColumn].filter(
            Boolean,
          ) as PropertiesConfigInputTableColumnModel[]) || []
        );
      }),
    );

    this.initMarginHandler((props) => props?.style?.table?.offset?.margin);

    this.observeSourceWorkflowOutcome();

    this.observeSourceWorkflowOutcome();

    this.tableColumnsVisibilityService
      .initializeExpressionContextForColumnVisibility(
        this.runtimeContext$,
        this.props$,
        this.dataSourceConnectedData$$.asObservable(),
      )
      .pipe(untilDestroyed(this))
      .subscribe((columns) => this.invisibleColumns.set(columns));

    effect(() => {
      this.updateDisplayedColumns(this.columns(), this.invisibleColumns());
      this.createTagColorConfig$$.next(this.displayedColumnsDef);
    });

    this.elementDataActions.visibleRowActions$
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((visibleRowActions) => {
        const anyActionAlwaysVisible = this.rowActions.some(
          (a) => !a.visibilityExpression?.length && !a.visible?.accessRoles?.length,
        );
        this.isAnyRowActionVisible.set(
          anyActionAlwaysVisible || !!visibleRowActions.some((row) => row.length),
        );
        this.updateDisplayedColumns(this.columns(), this.invisibleColumns());
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.firstValueArrived && changes['value']) {
      this.firstValueArrived = true;
    }

    if (changes?.['elementId'] && !this.elementKey) {
      this.elementKey = `${this.elementKeyRoot}-${this.elementId}`;
      this.elementDropdownKey = `${this.elementDropdownKeyRoot}-${this.elementId}`;
      this.elementInlineEditKey = `${this.elementInlineEditKeyRoot}-${this.elementId}`;
      this.elementActionsKey = `${this.elementActionsKeyRoot}-${this.elementId}`;
    }

    if (changes?.['props']) {
      const { content } = (this.props as TablePropsModel) || {};
      const { title, table } = content || {};

      this.labelEnabled = title?.enabled ?? true;
      this.icon = title?.icon || '';

      this.itemAdd = content?.itemAdd;

      this.noDataMessage =
        shouldShowEmptyDataMessage(this.props) && this.props?.content.table?.origin !== 'wizard'
          ? (table?.emptyTableLabel ?? DEFAULT_NO_DATA_MESSAGE)
          : '';

      this.rowActions = this.props?.content.unifiedRowActions ?? [];
      this.rowActionsHaveIcons = hasIcons(this.rowActions);
      this.elementDataActions.bulkActions = this.props?.content.unifiedBulkActions ?? [];
      this.elementDataActions.bulkActionsHaveIcons = hasIcons(this.elementDataActions.bulkActions);
      const otherActions = this.props?.content.unifiedOtherActions ?? [];
      this.elementDataActions.headerActionsHaveIcons = hasIcons([
        ...otherActions,
        ...this.elementDataActions.bulkActions,
      ]);
      this.elementDataActions.updateSelectionMode(!!this.elementDataActions.bulkActions.length);
      this.elementDataActions.resetSelectionModel();
      this.allActions$$.next([
        this.itemAdd,
        ...this.rowActions,
        ...otherActions,
        ...this.elementDataActions.bulkActions,
      ]);

      this.assignRowActiveRowClickAction();

      const autoSelectFirstRow = content.table.autoSelectFirstRow !== false;
      if (this.autoSelectFirstRow !== autoSelectFirstRow) {
        this.autoSelectFirstRow = autoSelectFirstRow;
        if (!autoSelectFirstRow && this.elementDataActions.focusCount === 1) {
          this.selection.clear();
          this.focus.clear();
        } else if (autoSelectFirstRow && !this.elementDataActions.focusCount) {
          this.tryInitialOutputUpdate();
        }
      }
      this.elementDataActions.singleSelect.set(!this.elementDataActions.bulkActions.length);
      this.setupSelection();
      this.updateDisplayedColumnsCollection();
      this.updateStyles();

      this.searchMode =
        this.props?.content.searchAndFilters?.mode ?? SearchAndFiltersMode.PREDEFINED;

      if (this.searchMode === SearchAndFiltersMode.FILTER_BUILDER && this.propsDataSource) {
        this.initComplexFilters(this.propsDataSource.id);
        this.loadSavedComplexFilters();
      }

      this.updateColumnsToEdit();
      this.updateLinkColumnsClickActions();
      this.updateConditionalInLineEdit();
    }

    if (changes['value'] || changes['props']) {
      // set sort

      const firstPropsSort = Array.isArray(this.props?.content?.sort)
        ? this.props.content.sort[0]
        : this.props?.content?.sort;
      const { field, direction } = firstPropsSort || {};

      let sort: SortValues | undefined;
      const firstChange = changes['props']?.firstChange;
      const sortExists = changes['props']?.previousValue?.content?.sort;
      const sortChanged = !isDeepEqual(
        changes['props']?.currentValue?.content?.sort,
        changes['props']?.previousValue?.content?.sort,
      );

      if (firstChange || (sortExists && sortChanged)) {
        sort =
          field && findColumn(this.props?.content.columns, this.schemaService.getColumnQuery(field)) // NOTE check if sort field exists
            ? {
                field: this.getColumnName(field) || '',
                direction: direction ?? 'asc',
              }
            : { field: '', direction: '' };
      }

      const groups = this.props?.content.table?.groupBy?.map((group) => group.field) || [];

      this.updateQuerySettings(this.props, sort, undefined, groups);
    }

    if (changes['value']) {
      this.removeAllGroups();
      this.wrapGroups(this.value);
      this.updateValue(this.value);
      this.updateSelection();
    } else if (!changes['value'] && !this.bindingResults?.['value']) {
      this.value = this.props?.content.previewData;
      this.removeAllGroups();
      this.wrapGroups(this.value);
      this.updateValue(this.value);
      this.updateSelection();
    }

    if (changes['value'] || changes['props']) {
      const dataSource = this.propsDataSource;
      this.dataSource$$.next(dataSource);
      this.updateColumnsExtremes();
    }

    if (changes['bindingResults']) {
      const valueBindingJustWiredUp =
        !changes['bindingResults'].previousValue?.['value'] && this.bindingResults?.['value'];

      // The previewData fallback above (`!changes['value'] && !this.bindingResults?.['value']`)
      // fires while a table's binding isn't resolved yet, e.g. mid AI generation. Once the
      // binding becomes configured, those sample rows no longer match the real columns and must
      // be cleared — but only on this false→true edge, never on a later truthy re-emission, or
      // already-loaded real rows would be wiped back out (ADR 0024).
      if (valueBindingJustWiredUp && !changes['value']) {
        this.value = [];
        this.removeAllGroups();
        this.wrapGroups(this.value);
        this.updateValue(this.value);
        this.updateSelection();
      }

      if (this.bindingResults?.['value'] && !!this.propsDataSource) {
        this.updateOutput(this.selection.selected || []);
      }
    }

    if (this.displayedColumns && this.displayedColumnsDef) {
      this.tableVirtualScrollFacade.updateDisplayedColumns(
        this.displayedColumns,
        this.displayedColumnsDef,
      );
      this.updateDisplayedColumnsCollection();
    }
    this.assignGroupColsFormats();

    this.tableVirtualScrollFacade.updateStickyColumnsVisibility(
      (this.props?.content?.table?.alwaysVisibleFirst &&
        this.props.content.table.alwaysVisibleColumnsCount) ||
        0,
      this.props?.content?.table?.alwaysVisibleActions ?? false,
    );

    this.idColumnName = this.dataValue.schema?.columns.find(({ isId }) => isId)?.name;

    const { shouldResetItemCache, shouldUpdateContentSize } =
      this.shouldRefreshVirtualScroll(changes);

    if (shouldResetItemCache || shouldUpdateContentSize) {
      this.ngZone.runOutsideAngular(() => {
        runAfterFramePaint(() => {
          if (shouldResetItemCache) {
            this.virtualScrollStrategy?.resetItemSizeCache();
          }

          this.virtualScrollStrategy?.updateContentSize();
          runAfterFramePaint(() => {
            this.tableVirtualScrollFacade.updateColumnsWidth();
          });
        });
      });
    }
  }

  ngOnInit(): void {
    if (this.propsDataSource) {
      this.initComplexFilters(this.propsDataSource.id);
    }

    this.getElementUiFeatures<ContainerElementMetaUiFeatures>()
      .pipe(untilDestroyed(this))
      .subscribe(({ title, columns }) => {
        this.labelText = title ?? '';
        this.columns.set(columns || []);

        this.updateStyles();
        safeDetectChanges(this.cdr);
      });
    this.stylesCleanup.register([this.elementKey, this.elementDropdownKey]);
    this.updateOutput(this.selection.selected || []);

    this.queryState$?.pipe(untilDestroyed(this)).subscribe((state) => {
      this.paginationProps = this.elementPaginationService.applyPaginationStateToModel(
        this.paginationProps,
        state,
      );
      this.totalRowsChanged = this.cachedTotalRowsCount !== (state?.totalCount || 0);
      this.cachedTotalRowsCount = state?.totalCount || 0;
      this.cdr.detectChanges();
    });

    /**
     * Declare output schemas when no value is bound to element.
     */
    if (!this.bindingResults?.['value']) {
      this.setDefaultEventSchema('row-select');
      this.setDefaultEventSchema('action');
    }

    /**
     * Declare output schemas when bindings change.
     */
    this.schema$.pipe(untilDestroyed(this)).subscribe((schema: BlazeSchema) => {
      this.setDefaultEventSchema(
        'row-select',
        convertBlazeSchemaToElementSchema(schema, this.elementId, 'row-select'),
      );
      this.setDefaultEventSchema(
        'action',
        convertBlazeSchemaToElementSchema(schema, this.elementId, 'action'),
      );
    });

    this.loadProjectIdFromState();

    this.initActionStyles(this.elementActionsKey, this.allActions$$.asObservable(), [
      'efh-action-btn',
      'action-btn',
    ]);
  }

  ngAfterViewInit(): void {
    this.cellsMap = mapOfTemplates(this.tpls.toArray(), 'CellTpl', { initials: 'image' });
    if (this.scrollable) {
      this.scrollableRegistry.register(this.scrollable);
      this.isVirtualScrollRegistered = true;
    }

    this.cdr.detectChanges();

    this.tryInitialOutputUpdate();
    this.watchConnections();
    this.tableVirtualScrollFacade.registerRootElement(
      this.hostEl.nativeElement.parentElement as HTMLElement,
      this.hostEl.nativeElement.querySelector('.table') as HTMLElement,
      this.hostEl.nativeElement.querySelector('.table-container') as HTMLElement,
    );
    this.onComponentVisibilityChange();
    this.onComponentResize();
    this.onWindowResize();
  }

  ngOnDestroy(): void {
    if (this.elementId) {
      // BL-9168: Ensure that filter builder query is reset when element is destroyed
      // (for example when opening and closing Blaze Table)
      this.store.dispatch(
        runtimeActions.updateElementQueryNode({
          elementId: this.elementId,
          query: undefined,
        }),
      );
    }

    this.query = undefined;
    this.stylesCleanup.dispose([
      this.elementKey,
      this.elementDropdownKey,
      this.elementInlineEditKey,
    ]);
    this.tableVirtualScrollFacade.onDestroy();
    this.componentDestroy$.next(true);
  }

  /**
   * Allow interacting with elements while preventing
   * element from movement in editor mode.
   * @param event
   */
  @HostListener('mousedown', ['$event'])
  onMouseDown(event: MouseEvent): void {
    let element: HTMLElement | null | undefined = event.target as HTMLElement;
    while (element && element !== this.hostEl.nativeElement) {
      if (element?.classList.contains('interactive')) {
        markEventAsHandled(event);
        break;
      } else {
        element = element?.parentElement;
      }
    }
  }

  onAction(params: OnContainerActionParams): Promise<ListLikeActionResult | undefined> | undefined {
    this.tableInlineEditorUtilsService.closeInlineEditor.next(true);
    return this.elementDataActions.onAction({
      ...params,
      output: this.output,
      dataRowSchema: this.getEventSchema('action'),
    });
  }

  onAddNewRow(): void {
    if (this.isAddingRowsDisabled) {
      return;
    }

    this.onAction({
      event: undefined,
      action: {
        id: ITEMS_CONTAINER_ACTION_ADD,
        label: this.itemAdd?.label ?? '',
      },
    });
  }

  /**
   * Checks if a column is a secure column
   * @param columnName column name resolved at runtime (actual column used)
   */
  isFieldSecure(columnName: string): boolean {
    return findColumn(this.dataValue.schema?.columns, columnName)?.isSecure ?? false;
  }

  //#region selection

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  toggleSelectAll(): void {
    if (isAllSelected(this.dataSource.data, this.paginationProps, this.selectionCount)) {
      this.elementDataActions.bulkAllSelected = false;
      this.selection.clear(true);
      this.focus.clear(true);
      this.cdr.markForCheck();
      return;
    }

    this.elementDataActions.bulkAllSelected = this.getNumberOfPages().numberOfPages === 1;
    this.selection.clear();
    this.focus.clear();
    if (this.paginationProps.isServerSide || !this.paginationProps.enabled) {
      this.selection.select(...this.dataSource.data);
      this.focus.select(...this.dataSource.data);
    } else {
      this.selection.select(...this.dataSourceOrderedData);
      this.focus.select(...this.dataSourceOrderedData);
    }
    this.cdr.markForCheck();
  }

  selectAllBulk(): void {
    this.elementDataActions.bulkAllSelected = true;
    this.selection.clear();
    this.focus.clear();
    this.selection.select(...this.dataSource.data);
    this.focus.select(...this.dataSource.data);
    this.updateBulkLabels();
  }

  //#endregion

  onPage(event: PageEvent): void {
    this.elementDataActions.bulkAllSelected = false;

    if (!this.paginationProps.isServerSide) {
      this.ngZone.runOutsideAngular(() => {
        runAfterFramePaint(() => {
          this.virtualScrollStrategy?.resetItemSizeCache();
        });
      });
      return;
    }

    const direction =
      event.pageIndex > (event.previousPageIndex || 0)
        ? PaginationDirection.Forwards
        : PaginationDirection.Backwards;
    this.updateQueryOutput(direction, event.pageIndex, this.query);
  }

  onSortChange(event: Sort): void {
    this.updateQuerySettings(this.props, {
      field: event.active,
      direction: event.direction,
    });
  }

  applyComplexFilter(formGroupValue: LogicalQueryFormGroupValue): void {
    this.query = formGroupValue as LogicalQueryFormGroupValue;
    this.updateQueryOutput(PaginationDirection.First, 0, this.query, undefined);
  }

  saveComplexFilter(filter: {
    name: string;
    scope: DatasetFilterScope;
    query: LogicalQueryNode;
  }): void {
    if (!this.projectId) {
      return;
    }

    this.savedFiltersLoading = true;
    this.cdr.markForCheck();

    this.datasetsApiService
      .createDatasetFilter({
        ...filter,
        queryNode: mapLogicalQueryNodeToLogicalPredicateNode(filter.query),
        projectId: this.projectId,
        uiComponentId: this.elementId,
      })
      .subscribe({
        next: (createdFilter) => {
          this.savedFilters = [...this.savedFilters, createdFilter];
          this.savedFiltersLoading = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.notificationService.error(
            error?.error?.errorMessage || 'There was an error. Please try again later.',
            true,
          );
          this.savedFiltersLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  deleteComplexFilter(filter: DatasetFilterInfo): void {
    this.datasetsApiService.deleteDatasetFilter(filter.id).subscribe();
    this.savedFilters = this.savedFilters.filter((f) => f.id !== filter.id);
    this.cdr.markForCheck();
  }

  resizeColumns(columns: PropertiesConfigInputTableColumnModel[]): void {
    const columnsForPropEditor = columns.filter((col) => !col.isGroupBy);
    this.updateProp.emit({
      path: 'content.columns',
      payload: columnsForPropEditor,
    });
    const groupColumn = columns.find((col) => col.isGroupBy);
    if (groupColumn && !isDeepEqual(groupColumn, this.props?.content.table?.groupByColumn)) {
      this.updateProp.emit({
        path: 'content.table.groupByColumn',
        payload: groupColumn,
      });
    }

    if (this.virtualScrollStrategy?.enableAutosize) {
      this.cdkViewport.checkViewportSize();
      this.virtualScrollStrategy?.toggleAutosize();
    }
  }

  columClicked(columnType: string, columnName: string, value?: string): void {
    if (this.cellsEditConfig?.[columnName]) {
      return;
    }

    switch (columnType) {
      case 'url':
        return this.dataTypeActions.openPage(value);
      case 'email':
        return this.dataTypeActions.copy(value);
      default:
        return;
    }
  }

  wrapClicked(items: GroupByRow | GroupByRow[]): void {
    items = Array.isArray(items) ? items : [items];
    items.forEach((item) => {
      const groupIndex = this.value.findIndex((val) => val.__id === item.__id);
      const nextGroupIndex = this.value.findIndex(
        (val, index) =>
          index > groupIndex && (val.__level === item.__level || val.__level < item.__level),
      );

      const lastIndexToCompare = nextGroupIndex === -1 ? this.value.length : nextGroupIndex;
      const recordsBetween = this.value.filter(
        (_, index) => index > groupIndex && index < lastIndexToCompare,
      );

      // we need to wrap this
      if (!this.wrappedGroupsIds.has(item.__id)) {
        recordsBetween.forEach((rec) =>
          this.wrappedRecordsIds.add(rec[this.getColIdName() ?? '__id'] ?? rec.__id),
        );
        this.wrappedGroupsIds.add(item.__id);
      } else {
        // we need to unwrap this
        recordsBetween.forEach((rec) =>
          this.wrappedRecordsIds.delete(rec[this.getColIdName() ?? '__id'] ?? rec.__id),
        );
        this.wrappedGroupsIds.delete(item.__id);
      }
    });
    this.updateDataSet(this.value, this.paginator);
    this.virtualScrollStrategy?.resetItemSizeCache();
    this.cdr.detectChanges();
  }

  onRowClick(event: Event | CellEditInfo, rowItem: unknown): void {
    if (isGroupByRow(rowItem)) {
      return;
    }

    if (this.actionOnRowClick && this.rowClickEnabled() && event instanceof Event) {
      // check if cell is editable
      const target = event.target as HTMLElement;
      const isSelectCell = !!target?.closest('.select-cell[role="cell"]');
      const isActionCell = !!target?.closest('.actions-cell[role="cell"]');
      const columnName = target
        ?.closest('[role="cell"][data-col-name]')
        ?.getAttribute('data-col-name') as string;
      const columnId = this.getColumnKeyForEditableCheck(columnName) || '';
      if (!isSelectCell && !isActionCell && !this.cellsEditConfig?.[columnId]) {
        this.triggerRowClickAction(event as Event, rowItem);
      }
    } else if (this.elementDataActions.bulkActions.length) {
      this.selection.clear();
      this.focus.clear();
      this.elementDataActions.toggleSelection(rowItem, this.value, { allowBulkOutputEmit: true });
      this.cdr.markForCheck();
    } else {
      this.elementDataActions.toggleSelection(rowItem, this.value);
      this.cdr.markForCheck();
    }
  }

  triggerRowClickAction(event: Event, item: unknown): void {
    if (typeof this.actionOnRowClick === 'string') {
      const action = {
        workflowId: this.actionOnRowClick,
      };
      this.executeRowClickAction(action, item);
    } else if (this.projectId) {
      this.pagesApi
        .getPages(this.projectId)
        .pipe(untilDestroyed(this))
        .subscribe((res) => {
          const id =
            typeof this.actionOnRowClick === 'string'
              ? this.actionOnRowClick
              : this.actionOnRowClick?.targetId;
          const page = res.find((page) => page.id === id);
          const propsPath = `content.rowClick.targetId.${this.actionOnRowClickId}`;
          if (!page) {
            setValueByPath(this.props, 'content.rowClick.enabled', false);
            setValueByPath(this.props, 'content.rowClick.action', undefined);
            setValueByPath(this.props, propsPath, undefined);
            return;
          }
          const action = {
            workflowId: id,
          };
          if (
            this.actionOnRowClick &&
            typeof this.actionOnRowClick !== 'string' &&
            this.actionOnRowClick.targetId !== page.pageType
          ) {
            setValueByPath(this.props, propsPath + '.targetType', page.pageType);
          }
          this.executeRowClickAction(action, item);
        });
    }
  }

  setActionOnHoverIndex(number: number | string | undefined): void {
    this.actionOnHoverIndex = number;
  }

  @HostListener('document:keypress', ['$event'])
  @HostListener('document:keydown', ['$event'])
  preventAction(event: KeyboardEvent): void {
    if (event.key !== 'Tab') {
      return;
    }
    if (!this.isInInlineEditMode) {
      return;
    }
    if (
      !this.hostEl.nativeElement.contains(document.activeElement) &&
      !document.activeElement?.closest('.cdk-overlay-pane')
    ) {
      return;
    }

    if (event.shiftKey && this.editableElementsForTab?.prev) {
      event.preventDefault();
      event.stopImmediatePropagation();
    } else if (!event.shiftKey && this.editableElementsForTab?.next) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  @HostListener('document:keyup', ['$event'])
  handleTabKey(event: KeyboardEvent): void {
    if (!this.isInInlineEditMode) {
      return;
    }
    if (event.key !== 'Tab') {
      return;
    }
    if (
      !this.hostEl.nativeElement.contains(document.activeElement) &&
      !document.activeElement?.closest('.cdk-overlay-pane')
    ) {
      return;
    }

    if (event.shiftKey && this.editableElementsForTab?.prev) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.tableInlineEditorUtilsService.closeInlineEditor.next(true);
      this.cellEdit$$.next(this.editableElementsForTab.prev);
    } else if (!event.shiftKey && this.editableElementsForTab?.next) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.tableInlineEditorUtilsService.closeInlineEditor.next(true);
      this.cellEdit$$.next(this.editableElementsForTab.next);
    } else {
      this.tableInlineEditorUtilsService.closeInlineEditor.next(true);
      this.cellEdit$$.next(undefined);
    }
  }

  cellMouseDownToggled(state: boolean): void {
    if (this.cellMouseDown$$.value !== state) {
      this.cellMouseDown$$.next(state);
    }
  }

  cellEditFromRow(item: CellEditEvent): void {
    return this.cellEdit(item.event, item.columnName, item.columnId, item.rowIndex, item.cell);
  }

  linkCellValueClick(
    event: {
      event: MouseEvent | FocusEvent;
      value: string;
      index: number;
    },
    columnName: string,
    columnId: string | undefined,
    rowNumber: number,
  ): void {
    let linkValue = this.dataSourceOrderedData[rowNumber][columnName];
    if (Array.isArray(linkValue)) {
      linkValue = linkValue.find(
        (val, index) => val.display === event.value && index === event.index,
      );
    }
    const pageId = this.linkCellValueClickConfig?.[columnId || columnName];
    if (!linkValue || !pageId || typeof pageId !== 'string') {
      return;
    }

    this.tableLinkCellActionService
      .prepareNavigateToTableLink(pageId, linkValue.rowId)
      .pipe(untilDestroyed(this))
      .subscribe(({ dataRow, dataRowSchema, page }) => {
        this.containerActionsService.handleListlikeAction({
          action: {
            enabled: true,
            id: ITEMS_CONTAINER_ACTION_LINK_CLICK,
            targetId: pageId,
            targetType: page?.pageType,
          },
          props: this.props,
          output: this.output,
          dataSource: {
            id: dataRowSchema?.id,
            sourceType: 'dataset',
            columns: [],
          },
          dataRow,
          dataRowSchema,
        } as ListLikeActionArgs);
      });
  }

  cellEdit(
    event: MouseEvent | FocusEvent,
    columnName: string,
    columnId: string | undefined,
    rowNumber: number,
    tableCell: HTMLElement,
    detailData?: CellValueUpdateModel['detailData'],
  ): void {
    if (!this.isInInlineEditMode) {
      return;
    }

    const columnSchema = this.dataValue.schema?.columns.find((col) =>
      columnId ? col.id === columnId : col.name === columnName,
    );
    const schemaColumnId = this.dataValue.schema?.columns.find((col) => col.isId)?.name;
    if (!schemaColumnId) {
      return;
    }

    const editColumnOnClick = columnSchema?.dataType && ['Boolean'].includes(columnSchema.dataType);
    if (editColumnOnClick) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.updateInlineRow(
        !this.dataSourceOrderedData[rowNumber][columnName],
        columnName,
        this.dataSourceOrderedData[rowNumber][schemaColumnId],
      );
      return;
    }

    this.tableInlineEditorUtilsService.closeInlineEditor.next(true);
    event.preventDefault();
    event.stopImmediatePropagation();
    this.cellEdit$$.next({ columnName, columnId, rowNumber, tableCell, detailData });
  }

  tagColumnClicked(
    event: MouseEvent | FocusEvent,
    columnName: string,
    columnId: string | undefined,
    rowNumber: number,
    tableCell: HTMLElement,
    detailData?: CellValueUpdateModel['detailData'],
  ): void {
    if (isCellUpdateFromTag(detailData) && detailData.actionType) {
      this.handleTagOrLinkActionInRow(columnName, rowNumber, detailData.tag, detailData.actionType);
    } else if (!isCellUpdateFromTag(detailData)) {
      this.cellEdit(event, columnName, columnId, rowNumber, tableCell, detailData);
    }
  }

  private initializeConditionalFormatters(): void {
    this.formatters$ = combineLatest({
      conditionalFormatting: this.props$.pipe(
        map((props) => props?.content.conditionalFormatting),
        distinctUntilChanged(isDeepEqual),
      ),
      context: this.runtimeContext$.pipe(distinctUntilChanged(isDeepEqual)),
    }).pipe(
      map(({ conditionalFormatting, context }) => {
        return (
          item: any | null,
          columnKey: string,
        ): PropertiesConfigInputTableConditionalFormatModel | undefined => {
          if (!conditionalFormatting?.enabled) {
            return;
          }
          return conditionalFormatting.formats.find(
            (format) =>
              !!format.conditions.conditions?.length &&
              (format.columnSelection === TableConditionalFormatColumnSelection.all ||
                format.columns.includes(columnKey)) &&
              this.queryExpression('when', format.conditionsExpression, {
                ...context,
                output: item,
              }),
          );
        };
      }),
    );
  }

  private updateQuerySettings(
    currentProps?: PropsOf<'table'>,
    sort: SortValues = this.sortValues,
    query: LogicalQueryNode | undefined = this.query,
    groups: string[] | undefined = this.groups,
  ): void {
    if (!currentProps) {
      return;
    }

    const paginationProps = this.elementPaginationService.getTablePaginationProps(currentProps);
    const enabledChanged = this.paginationProps.enabled !== paginationProps.enabled;
    const pageSizeChanged = this.paginationProps.pageSize !== paginationProps.pageSize;
    const sortChanged =
      sort?.field !== this.sortValues.field || sort?.direction !== this.sortValues.direction;
    const groupsChanged =
      (groups?.length ?? 0) !== (this.groups?.length ?? 0) ||
      !!this.groups?.some((group) => !groups?.includes?.(group));
    const needsRefresh =
      !this.value?.length &&
      (this.paginationProps.pageIndex > 0 || this.paginationProps.requireRefresh);

    this.paginationProps.enabled = paginationProps.enabled;
    this.paginationProps.pageSize = paginationProps.pageSize;

    this.groups = groups;

    this.sortValues = sort || { field: '', direction: '' };
    this.updateSort();

    const handleSortChangeOnClient = sortChanged && this.shouldUseClientPagination;
    const handleSortChangeOnServer = sortChanged && !this.shouldUseClientPagination;

    if (handleSortChangeOnClient) {
      this.updateDataSet(this.value, this.paginator);
    }
    if (
      enabledChanged ||
      pageSizeChanged ||
      groupsChanged ||
      needsRefresh ||
      handleSortChangeOnServer
    ) {
      this.updateQueryOutput(PaginationDirection.First, 0, query, groups);
    }
  }

  private updateValue(value?: any[]): void {
    // Server pagination
    if (this.paginationProps.isServerSide) {
      // Prevent content jumping when changing pagination mode to fixed page sizes.
      const valueToSet =
        this.paginationProps.enabled && Array.isArray(value)
          ? value?.slice(0, this.paginationProps.pageSize)
          : value;
      this.updateDataSet(valueToSet);
      this.ngZone.runOutsideAngular(() => {
        runAfterFramePaint(() => {
          this.virtualScrollStrategy?.resetItemSizeCache();
        });
      });
      return;
    }

    // Client pagination
    if (this.paginatorTimeout !== undefined) {
      clearTimeout(this.paginatorTimeout);
      this.paginatorTimeout = undefined;
    }

    if (!this.paginator) {
      this.paginatorTimeout = setTimeout(() => {
        this.updateDataSet(value, this.paginator);
        this.cdr.detectChanges();
      });
    } else {
      this.updateDataSet(value, this.paginator);
      this.virtualScrollStrategy?.resetItemSizeCache();
    }
  }

  private removeAllGroups(): void {
    this.wrappedGroupsIds.clear();
    this.wrappedRecordsIds.clear();
  }

  private wrapGroups(value: any): void {
    if (this.collapseGroupsByDefault && !this.wrappedGroupsIds.size && Array.isArray(value)) {
      this.wrapClicked(value.filter((v) => v.__id && v.__level === 0));
    }
  }

  private updateColumnsExtremes(): void {
    this.columnsExtremes.set(
      computeColumnExtremes(
        this.props?.content.columns,
        this.value,
        this.dataValue.schema,
        (column) => this.schemaService.getColumnNameLatest(column),
      ),
    );
  }

  /**
   * Resolves the table's own bound source workflow outcome (ADR 0024): starts the workflow if
   * nothing else did, and resolves the empty state to "No data" once nothing is going to deliver
   * rows. Never regresses a count that has already loaded — only ever resolves undefined to 0.
   */
  private observeSourceWorkflowOutcome(): void {
    this.sourceWorkflowOutcomeService
      .observeOutcome$(this.dataSource$$.asObservable())
      .pipe(untilDestroyed(this))
      .subscribe((outcome) => {
        if (
          this.dataRowsCount === undefined &&
          (outcome === 'unstarted' || outcome === 'settledWithoutOutput')
        ) {
          this.dataRowsCount = 0;
          this.cdr.detectChanges();
        }
      });
  }

  private updateDataSet(value: any, paginator?: PaginatorComponent): void {
    if (!value) {
      value = [];
    } else if (!Array.isArray(value)) {
      value = [value];
    }

    const cleanedData = (value as typeof this.value).filter(
      (val) => !this.wrappedRecordsIds.has(val[this.getColIdName() ?? '__id'] ?? val.__id),
    );

    this.dataSource.data = cleanedData;
    this.cdr.detectChanges();

    this.dataSource.paginator = paginator ?? null;
    if (paginator) {
      paginator.pageSize = this.paginationProps.pageSize;
    }
    this.dataRowsCount = value?.length ?? 0;
    const sortDataFn = this.value?.some?.(isGroupByRow)
      ? this.sortDataFnGroupBy
      : this.sortDataFnDefault;
    if (this.dataSource.sortData !== sortDataFn) {
      this.dataSource.sortData = sortDataFn;
    }
    this.dataSource.sortingDataAccessor = createSortingDataAccessor();

    // Prevent applying client side sort and server side sort at the same time
    this.dataSource.sort =
      this.shouldUseClientPagination && this.sortValues.field ? this.sort : null;

    if (this.selectionCount || this.elementDataActions.focusCount) {
      const idColumn = getDataSourceIdColumn(this.propsDataSource, this.schemaService) ?? '';
      const selected =
        this.selectionCount && idColumn
          ? this.value?.find(
              (row) => row[idColumn] === (this.selection.selected[0] as any)?.[idColumn],
            )
          : undefined;

      if (selected) {
        this.selection.selected[0] = selected;
      } else {
        this.selection.clear();
      }

      const focused =
        this.elementDataActions.focusCount && idColumn
          ? this.value?.find((row) => row[idColumn] === (this.focus.selected[0] as any)?.[idColumn])
          : undefined;

      if (focused) {
        this.focus.selected[0] = focused;
      } else {
        this.focus.clear();
      }
    }
    this.tryInitialOutputUpdate();
    this.virtualScrollStrategy?.resetItemSizeCache();
  }

  private updateDisplayedColumns(
    columnsProps: ContainerFieldElementMetaUiFeatures[] | undefined,
    invisibleColumns: string[],
  ): void {
    // Prepare column definitions with latest known column names
    const visibleTableColumns = (columnsProps ?? [])
      .map((colProps) => {
        const column = colProps.column as PropertiesConfigInputTableColumnModel;
        const result: PropertiesConfigInputTableColumnModel = { ...column };
        result.label = colProps.name;
        result.format = colProps.format;
        result.precision = colProps.precision;
        result.currency = colProps.currency;
        result.duration = colProps.duration;

        if (result.visible) {
          result.name = this.schemaService.getColumnNameLatest(column);
        }
        return result;
      })
      .filter(
        (col) =>
          !!col.name &&
          col.visible &&
          !invisibleColumns.some(
            (invisibleCol) => invisibleCol === col.id || invisibleCol === col.name,
          ),
      );

    if (this.props?.content.table?.groupBy.some((col) => col.field)) {
      visibleTableColumns.unshift({
        format: 'text',
        label: 'GroupBy',
        name: GROUP_BY_KEY,
        isGroupBy: true,
      });
    } else if (
      !this.props?.content.table?.groupBy.some((col) => col.field) &&
      visibleTableColumns.some((col) => col.name === GROUP_BY_KEY)
    ) {
      visibleTableColumns.splice(
        visibleTableColumns.findIndex((col) => col.name === GROUP_BY_KEY),
        1,
      );
    }

    // Make sure unique names are used only
    this.displayedColumnsDef = uniqBy(visibleTableColumns, (col) => col.name);
    this.originalColumnSizes = {};
    for (const col of this.displayedColumnsDef) {
      if (!col.width) {
        continue;
      }

      this.originalColumnSizes[col.name] = col.width;
    }
    // Display a warning if name conflicts were found
    if (visibleTableColumns.length !== this.displayedColumnsDef.length) {
      console.warn(
        '[Table] Column name conflict detected - some columns might not be displayed. Refresh columns to fix.',
        visibleTableColumns,
        this.displayedColumnsDef,
      );
    }

    const displayedColumnsNames = this.displayedColumnsDef.map((column) => column.name);
    this.updateDisplayedColumnsCollection([...displayedColumnsNames]);

    this.tableVirtualScrollFacade.updateDisplayedColumns(
      this.displayedColumns,
      this.displayedColumnsDef,
    );
  }

  private updateQueryOutput(
    direction?: PaginationDirection,
    index = 0,
    query: LogicalQueryNode | undefined = undefined,
    groups: (ColumnId | string)[] | undefined = undefined,
  ): void {
    const dataSource = this.propsDataSource;
    if (!dataSource) {
      return;
    }

    const data: RuntimeEventQueryData = {
      page: { size: this.paginationProps.pageSize, index, direction },
      elementId: this.elementId as string,
      dataSource,
      query,
      groups,
    };

    if (this.sortValues.field) {
      data.sort = { columnName: this.getColumnName(this.sortValues.field) };
      if (this.sortValues.direction) {
        data.sort.direction = sortMap[this.sortValues.direction] ?? '';
      }
    }

    const shouldEmit = this.paginationEmitTracker.shouldEmit(data);
    if ((this.firstValueArrived && shouldEmit) || this.totalRowsChanged) {
      this.output.emit({
        eventType: 'query',
        data,
        output: 'pagination',
        isInitialData: false,
      });
    } else if (!this.firstValueArrived && shouldEmit) {
      // Update query has been requested but the table wasn't ready to emit.
      this.query = query;
    }
  }

  private tryInitialOutputUpdate(): void {
    if (
      !this.dataRowsCount ||
      this.elementDataActions.focusCount /* already has selection */ ||
      !this.autoSelectFirstRow
    ) {
      return;
    }

    const elementToWorkflowsConnections = this.runtimeService
      .getConnectionsBySourceAndEventType(
        {
          id: this.elementId,
          layerType: 'Ui',
        },
        'row-select',
      )
      ?.filter((c) => c.target.layerType === 'Workflow');

    if (elementToWorkflowsConnections.length === 0) {
      return;
    }

    if (!this.elementDataActions.focusCount && this.dataSourceOrderedData.length) {
      const row = this.dataSourceOrderedData.find((row) => !isGroupByRow(row));
      this.focus.select(row);
    }
    this.cdr.detectChanges();
  }

  private watchConnections(): void {
    // the connections from the table to workflows are observed
    // to trigger row auto-selection
    this.store
      .select(runtimeSelectors.selectConnections)
      .pipe(
        untilDestroyed(this),
        map((connections) =>
          connections
            .filter(
              (c) =>
                c.source.layerType === 'Ui' &&
                c.source.id === this.elementId &&
                c.eventType === 'row-select' &&
                c.target.layerType === 'Workflow',
            )
            .map(({ id }) => id),
        ),
        distinctUntilChanged(),
      )
      .subscribe(() => {
        this.tryInitialOutputUpdate();
      });
  }

  private updateOutput(data: unknown[], addedData?: any): void {
    if (!this.isAddingCheckInitialized) {
      const isAddingAllowed = this.containerActionsService.isAddingRowsAllowed(
        this.propsDataSource,
      );
      if (typeof isAddingAllowed === 'boolean') {
        this.isAddingRowsDisabled = !isAddingAllowed;
        this.isAddingCheckInitialized = 'primitive';
      } else {
        isAddingAllowed.pipe(untilDestroyed(this)).subscribe((value) => {
          this.isAddingRowsDisabled = !value;
          this.cdr.detectChanges();
        });
        this.isAddingCheckInitialized = true;
      }
    } else if (this.isAddingCheckInitialized === 'primitive') {
      this.isAddingRowsDisabled = !this.containerActionsService.isAddingRowsAllowed(
        this.propsDataSource,
      ) as boolean;
    }

    let isEmptyishData = !data;
    // We need to support two different behaviors when it comes to selected row:
    if (Array.isArray(data) && !data.length && Array.isArray(addedData) && !addedData?.length) {
      // 1. Change in the UI resulted in the table changing from populated table to empty table. In such a case,
      //    addedData is expected to be []. If that's the case, we set the data to an empty data row so that any workflows connected
      //    to the table can run and update UI components (BL-1639).
      data = this.containerActionsService.getEmptyRow(this.propsDataSource) as any;
      isEmptyishData = true;
    } else if (Array.isArray(data)) {
      // 2. Table loads and hasn't had a chance to set the data yet. In such a case, we need to block any workflows
      //    connected to the table so that they don't show incorrect data. We achieve that by having the data
      //    set to undefined.
      data = data[data.length - 1] as any;
    }

    /**
     * Do not emit event when no value is bound. It prevents emitting incorrect schema
     * for example data (stored in element's props).
     * Generally element should not emit output when there is no input declared.
     */
    if (this.outputEmitTracker.shouldEmit(data) && !!this.bindingResults?.['value']) {
      const schema = this.dataValue.schema
        ? convertBlazeSchemaToElementSchema(this.dataValue.schema, this.elementId, 'row-select')
        : undefined;

      if (!isNullish(data) && !isNullish(schema)) {
        const dataToEmit = this.removeTempIdFromData(data);
        this.output.emit({
          eventType: 'row-select',
          data: dataToEmit,
          schema,
          isInitialData: false,
          isEmptyishData,
        });
      }
    }
  }

  private updateSort(): void {
    if (this.sort && this.sortValues.field) {
      this.sort.active = this.getColumnName(this.sortValues.field) || '';
      this.sort.direction = this.sortValues.direction;
    }
  }

  private updateDisplayedColumnsCollection(displayedColumnsNames?: string[]): void {
    displayedColumnsNames ??= [...this.displayedColumns];

    if (this.props?.content.table?.groupBy.some((group) => group.field)) {
      this.displayedColumnsDef = [
        this.props.content.table.groupByColumn,
        ...this.displayedColumnsDef.filter((col) => !col.isGroupBy),
      ];
      if (displayedColumnsNames.indexOf(this.props.content.table.groupByColumn.name) === -1) {
        displayedColumnsNames.unshift(this.props.content.table.groupByColumn.name);
      }
    } else if (
      !this.props?.content.table?.groupBy.some((group) => group.field) &&
      this.displayedColumnsDef.find((col) => col.isGroupBy)
    ) {
      this.displayedColumnsDef = this.displayedColumnsDef.filter((col) => !col.isGroupBy);

      const groupColIndex = displayedColumnsNames.findIndex(
        (col) => col === this.props?.content.table?.groupByColumn.name,
      );
      if (groupColIndex !== -1) {
        displayedColumnsNames.splice(groupColIndex, 1);
      }
    }

    // Add/remove selection column if needed
    addOrRemoveDisplayedColumnMutably(
      displayedColumnsNames,
      this.selectColumnName,
      !!this.elementDataActions.bulkActions.length || this.hasRowNumbering(),
      true,
    );
    // Add/remove actions column if needed
    addOrRemoveDisplayedColumnMutably(
      displayedColumnsNames,
      this.actionsColumnName,
      this.isAnyRowActionVisible() ? !!this.rowActions.length : false,
    );

    this.displayedColumns = displayedColumnsNames;
    this.cdr.markForCheck();
  }

  private initComplexFilters(workflowId: string): void {
    if (this.complexFiltersInitialized || this.props?.content.table?.origin === 'wizard') {
      return;
    }

    this.complexFiltersInitialized = true;
    const schemaChange$ = this.dataValue.schema$.pipe(
      filter(isWorkflowSchema),
      countedShareReplay(),
      distinctUntilChanged((a, b) => a.tableId === b.tableId),
    );

    combineLatest({
      getNode: this.store
        .select(runtimeSelectors.selectFirstNodeInWorkflow(workflowId, isGetNode))
        .pipe(distinctUntilChanged(isDeepEqual), filter(Boolean)),
      filterBuilderRestriction: this.props$.pipe(
        map((props) => props?.content.filterBuilderConfig.columns),
        distinctUntilChanged(isDeepEqual),
      ),
    })
      .pipe(untilDestroyed(this))
      .subscribe(({ getNode, filterBuilderRestriction }) => {
        this.complexFilterService.setup(getNode, filterBuilderRestriction);
      });

    schemaChange$.pipe(skip(1), untilDestroyed(this)).subscribe(() => {
      if (this.filterBuilderComponent) {
        this.filterBuilderComponent.resetForm();
      }
    });
  }

  //#region selection

  private setupSelection(): void {
    if (
      !this.selection ||
      this.selection.isMultipleSelection() !== !this.elementDataActions.singleSelect()
    ) {
      if (this.selectionSubscription) {
        this.selectionSubscription.unsubscribe();
      }

      this.elementDataActions.resetSelectionModel();
    }

    if (this.selection.changed.observed) {
      return;
    }

    this.selectionSubscription = this.focus.changed
      .pipe(
        // Make sure this.selection.clear and this.selection.select() run one after another don't trigger
        // the subscription twice.
        debounceTime(0),
        map((selection: any) => ({
          addedData: selection.added,
          selectedData: selection.source.selected.filter(
            (data: typeof this.value) => !isGroupByRow(data),
          ),
        })),
        delayUntil(() => this.dataValue.schema$.pipe(filter(Boolean))),
        untilDestroyed(this),
      )
      .subscribe(({ addedData, selectedData }) => {
        this.updateBulkLabels();
        if (this.elementDataActions.outputEmitAllowed) {
          this.updateOutput(selectedData, addedData);
        }
        this.elementDataActions.outputEmitAllowed = true;
        this.cdr.detectChanges();
      });
  }

  private updateSelection(): void {
    if (!this.selectionCount && !this.elementDataActions.focusCount) {
      return;
    }
    const idKey = determineIdColumnNameOfDatasource(this.propsDataSource?.columns);
    if (!idKey) {
      return;
    }

    const selected = this.value?.filter((v) =>
      this.selection.selected.some((s) => (s as any)?.[idKey] === v[idKey]),
    );
    const focused = this.value?.filter((v) =>
      this.focus.selected.some((s) => (s as any)?.[idKey] === v[idKey]),
    );
    this.selection.clear();
    this.focus.clear();
    this.selection.select(...selected);
    this.focus.select(...focused);
  }

  private updateBulkLabels(): void {
    this.bulkSelectionLabel = this.getBulkSelectionLabel();
    this.bulkSelectionTriggerLabel = this.getBulkSelectionTriggerLabel();
    safeDetectChanges(this.cdr);
  }

  private getBulkSelectionTriggerLabel(): string | undefined {
    if (
      this.elementDataActions.singleSelect() ||
      this.elementDataActions.bulkAllSelected ||
      !this.selectionCount
    ) {
      return undefined;
    }

    const { numberOfPages, totalCount } = this.getNumberOfPages();
    if (numberOfPages === undefined) {
      return 'Select all rows in all pages';
    }
    if (numberOfPages > 1) {
      return `Select all ${totalCount} rows in ${numberOfPages} pages`;
    }
    return undefined;
  }

  private getBulkSelectionLabel(): string | undefined {
    if (this.elementDataActions.singleSelect()) {
      return undefined;
    }
    if (this.elementDataActions.bulkAllSelected) {
      return this.totalCount
        ? `All ${this.totalCount} row${this.totalCount > 1 ? 's' : ''} selected`
        : 'All rows selected';
    }
    const selectionCount = this.selectionCount;
    if (!selectionCount) {
      return undefined;
    }
    return `${selectionCount} row${selectionCount > 1 ? 's' : ''} selected`;
  }

  private getNumberOfPages(): { totalCount?: number; numberOfPages?: number } {
    const totalCount = this.cachedTotalRowsCount || this.dataSource.data.length;
    if (!totalCount) {
      return {};
    }

    const { pageSize, enabled } = this.paginationProps;
    if (!enabled || !pageSize) {
      return { numberOfPages: 1 };
    }
    return {
      totalCount,
      numberOfPages: Math.ceil(totalCount / pageSize),
    };
  }

  //#endregion

  private onComponentVisibilityChange(): void {
    intersectionRxUtils
      .fromIntersectionObserver(this.hostEl.nativeElement)
      .pipe(
        pairwise(),
        filter(
          ([previous, current]) =>
            previous.length > 0 &&
            current.length > 0 &&
            previous[0].boundingClientRect.height === 0 &&
            current[0].boundingClientRect.height !== 0,
        ),
        untilDestroyed(this),
      )
      .subscribe(() => {
        if (this.virtualScrollStrategy) {
          this.virtualScrollStrategy.toggleAutosize();
        }
      });
  }

  private onComponentResize(): void {
    const tableWrapper = this.tableWrapper?.nativeElement;
    if (!tableWrapper) {
      return;
    }

    combineLatest([
      resizeRxUtils.fromResizeObserver(tableWrapper),
      toObservable(this.tableVirtualScrollFacade.rootElementResize, { injector: this.injector }),
    ])
      .pipe(auditTime(0, animationFrameScheduler), untilDestroyed(this))
      .subscribe(() => {
        this.tableWrapperRect = tableWrapper.getBoundingClientRect();
        this.cdr.markForCheck();

        if (this.virtualScrollStrategy && this.rowHeightBehavior === 'auto') {
          // Only reset when the wrapper width changes. The CDK virtual-scroll spacer
          // changes the wrapper height as row heights are measured, which also fires
          // the ResizeObserver. Resetting on every height change clears the size cache
          // each frame and creates an infinite resize→reset→resize loop.
          const currentWidth = tableWrapper.getBoundingClientRect().width;
          if (currentWidth !== this._lastTableWrapperWidth) {
            this._lastTableWrapperWidth = currentWidth;
            this.virtualScrollStrategy.resetItemSizeCache();
          }
        }
      });

    toObservable(this.tableVirtualScrollFacade.viewportRefresh, { injector: this.injector })
      .pipe(auditTime(0, animationFrameScheduler), untilDestroyed(this))
      .subscribe(() => {
        if (this.tableRows) {
          for (const row of this.tableRows) {
            row.checkViewportSize();
          }
        }
      });
  }

  private onWindowResize(): void {
    fromEvent(window, 'resize')
      .pipe(debounceTime(100), untilDestroyed(this))
      .subscribe(() => {
        if (this.tableWrapper) {
          this.tableWrapperRect = this.tableWrapper.nativeElement.getBoundingClientRect();
          this.cdr.markForCheck();
        }
      });
  }

  private updateStyles(): void {
    const { content, style } = this.props ?? {};
    const { title, table: tableContent } = content ?? {};
    const {
      general,
      header,
      table,
      fontSize,
      fontFamily,
      lineHeight,
      submenu,
      booleanCol,
      container,
    } = style ?? {};
    const { actionsDisplayMode, bulkActionsDisplayMode } = tableContent ?? {};
    if (!title || !general || !header || !table || !submenu || !booleanCol || !container) {
      return;
    }

    if (!!table.rowNumbering !== this.hasRowNumbering()) {
      this.hasRowNumbering.set(!!table.rowNumbering);
    }

    // Host bindings
    this.internalPadding = table.offset.padding;

    this.accent = general.accent.color1;
    this.borderColor =
      (table.lines.tableLinesOptions ?? table.lines.selectorType) === 'none'
        ? 'transparent'
        : table.lines.color1;
    const borderColorForAction = table.lines.color1;
    let tableLinesOptions = table.lines.tableLinesOptions;
    if (!tableLinesOptions) {
      if (table.lines.selectorType === 'none') {
        tableLinesOptions = 'none';
      } else if (table.hideColumnsBorder) {
        tableLinesOptions = 'row-outline';
      } else {
        tableLinesOptions = 'all';
      }
    }
    this.borderColorHeader = ['none', 'outline'].includes(tableLinesOptions)
      ? 'transparent'
      : table.lines.color1;
    this.borderColorOutline = ['none', 'header', 'row'].includes(tableLinesOptions)
      ? 'transparent'
      : table.lines.color1;
    this.borderColorRow = ['none', 'outline', 'header'].includes(tableLinesOptions)
      ? 'transparent'
      : table.lines.color1;
    this.borderColorCell = ['none', 'outline', 'row-outline'].includes(tableLinesOptions)
      ? 'transparent'
      : table.lines.color1;
    this.borderRadius = table.radius;
    this.fontSize = fontSize as string;
    this.fontFamily = fontFamily as string;
    this.lineHeight = lineHeight as string;
    this.lineClamp = table.rowLineClamp ?? 1;
    this.headerColor = header.label.color1;
    this.headerBackground = header.background.color1;
    this.cellColor = table.text.color1;
    this.cellBackground = table.background.color1;
    this.cellHoverBackground = table.hover.color1;
    this.cellSelectedRowBackground = table.selectedRow.color1;
    this.lightCellColor = setAlphaToHexColor(table.text.color1, 63);
    this.lighterCellColor = setAlphaToHexColor(table.text.color1, 50);
    this.lightestCellColor = setAlphaToHexColor(table.text.color1, 35);

    this.titleColor = general.title.color1;
    this.wrapperStyles = {
      ...makeContainerVisualEffectStyles(general.visualEffect),
      ...makeBorderStyles(container.outline, container.outlineWidth),
    };

    this.rowSize = table.rowHeight ?? 'small';
    this.rowActionsDisplayMode = actionsDisplayMode ?? 'always';
    this.rowHeightBehavior = table.rowHeightBehavior ?? 'fixed';
    this.rowActionsGrouped = actionsDisplayMode === 'group';

    this.elementDataActions.bulkActionsDisplayMode = bulkActionsDisplayMode ?? 'always';
    this.elementDataActions.bulkActionsGrouped = bulkActionsDisplayMode === 'group';

    this.fileLineClamp =
      this.rowHeightBehavior === 'auto'
        ? 'none'
        : TABLE_FILE_LINE_CLAMP_MAPPING[table.rowLineClamp];

    this.colBoolOnBg = booleanCol.checkedBackground.color1;
    this.colBoolOnMark = booleanCol.checkedMark.color1;
    this.colBoolOffBg = booleanCol.uncheckedMark.color1;
    this.colBoolOffMark = booleanCol.uncheckedBackground.color1;

    const groupStyles = this.props?.style.table?.group;
    if (groupStyles) {
      this.groupsStyling.groupColors =
        groupStyles.colorGenOptions === 'preselected'
          ? [groupStyles.bgLevel1.color1, groupStyles.bgLevel2.color1, groupStyles.bgLevel3.color1]
          : [
              groupStyles.bgBaseColor.color1,
              setAlphaToHexColor(groupStyles.bgBaseColor.color1, 75),
              setAlphaToHexColor(groupStyles.bgBaseColor.color1, 50),
            ];
      this.groupsStyling.fontStyles = {
        color: groupStyles.text.color1,
        ...makeFontStyles({ fontSize: groupStyles.fontSize }),
        ...makeFontStyleStyles(groupStyles.style, '500'),
        ['font-weight']: this.fontsService.getFontWeight(
          groupStyles.fontFamily,
          groupStyles.fontWeight,
        ),
      };
    }

    const cf = this.props?.content.conditionalFormatting;
    this.stylesCleanup.update(
      this.elementKey,
      `
      .${this.elementKey} .header-cell {
        ${table.hideColumnsBorder ? '' : `border-left: 1px solid ${this.borderColorCell};`}
      }
      ${table.hideColumnsBorder ? '' : `.${this.elementKey} .header-cell:first-child { border-left: 0 }`}
      .${this.elementKey} .table-body .row.selected .cell .cell-container {
        background: ${table.selectedRow.color1};
      }
      .${this.elementKey} .table-body .row:hover .cell:not(.cell-no-data):not([grouping-level]) .cell-container {
        background: ${table.hover.color1};
      }
      .${this.elementKey} .header-cell-container {
        color: ${header.label.color1};
      }
      .${this.elementKey} .cell {
        color: ${table.text.color1};
        ${table.hideColumnsBorder ? '' : `border-left: 1px solid ${this.borderColorCell};`}
        ${
          !cf?.enabled
            ? ''
            : cf.formats
                .map(
                  (format) => `&.conditional-${format.id} {
            ${makeCssStringFromObj(tableConditionalFormatStyle(format, true))};
            .icon {
              ${makeCssStringFromObj(tableConditionalFormatIconStyle(format))}
            }
          }
          &.conditional-meta-${format.id} {
            ${makeCssStringFromObj(tableConditionalFormatStyle(format, false))};
          }`,
                )
                .join('\n')
        }
      }

      ${table.hideColumnsBorder ? '' : `.${this.elementKey} .cells:first-child .cell:first-child { border-left: 0; }`}

      ${table.hideColumnsBorder ? '' : `.${this.elementKey} .cells.slot-left .cell:last-child { border-right: 1px solid ${this.borderColorCell}; }`}

      .${this.elementKey} .cells.slot-left + .cells .cell:first-child { border-left: 0 }

      .${this.elementKey} blaze-checkbox.base-checkbox input[type='checkbox'] {
        background-color: ${header.background.color1};
        border: 1px solid ${this.borderColor} !important;
        background-size: 8px;
      }
      .${this.elementKey} blaze-checkbox.base-checkbox:hover input[type='checkbox'] {
        border-color: ${this.accent} !important;
      }
      .${this.elementKey} blaze-checkbox.base-checkbox input[type='checkbox']:checked {
        background-color: ${this.accent} !important;
        border-color: ${this.accent} !important;
      }
      .${this.elementKey} blaze-checkbox.base-checkbox input[type='checkbox']:checked + mat-icon {
        color: ${table.background.color1};
      }

      .${this.elementKey} .cell-inline-editable:hover .cell-container {
        outline-color: ${borderColorForAction};
        box-shadow: -2px 0px 0 ${borderColorForAction} inset, 2px 0px 0 ${borderColorForAction} inset;
      }

      .${this.elementKey} .row:last-of-type .cell-inline-editable:hover .cell-container {
        outline-color: ${borderColorForAction};
        box-shadow: -2px 0px 0 ${borderColorForAction} inset, 2px 0px 0 ${borderColorForAction} inset, 0px -1px 0 ${borderColorForAction} inset;
      }

      .${this.elementKey} .row-click-action-hover:hover .cell-container {
        box-shadow: 0px -1px 0 ${this.borderColor} inset, 0px 1px 0 ${this.borderColor} inset;
        cursor: pointer;
      }

      .${this.elementKey} .row-click-action-hover:hover .cell:first-of-type .cell-container {
        box-shadow: 1px -1px 0 ${this.borderColor} inset, 0px 1px 0 ${this.borderColor} inset;
      }

      .${this.elementKey} .row-click-action-hover:hover .cell:last-of-type .cell-container {
        box-shadow: 0px -1px 0 ${this.borderColor} inset, -1px 1px 0 ${this.borderColor} inset;
      }

      .${this.elementKey} .btn-bulk-select-all {
        color: ${general.accent.color1};
      }

      .${this.elementKey} .grouping-row .cell-content {
        color: ${this.groupsStyling.fontStyles?.color || undefined};
        font-size: ${this.groupsStyling.fontStyles?.['font-size'] || ''};
        font-style: ${this.groupsStyling.fontStyles?.['font-style'] || ''};
        line-height: ${this.groupsStyling.fontStyles?.['line-height'] || ''};
        text-decoration: ${this.groupsStyling.fontStyles?.['text-decoration'] || ''};
        font-weight:${this.groupsStyling.fontStyles?.['font-weight'] || 'normal'};
      }

      .${this.elementKey} .grouping-row .cell-content .collapse-icon {
        scale: calc( ${parseInt(this.groupsStyling.fontStyles?.['font-size']) || 13} / 13 );
      }

      .${this.elementDropdownKey} button:hover { span { color: currentColor !important; }}
    `,
    );

    const accent10 = `${general.accent.color1.substring(0, 7)}0A`;

    this.stylesCleanup.update(
      this.elementDropdownKey,
      `

    .${this.elementDropdownKey} button.mat-mdc-menu-item {
      color: ${submenu.value.color1};
      font-size: ${style?.fontSize};
    }
    .${this.elementDropdownKey} button:hover {
      color: ${general.accent.color1};
    }
    .${this.elementDropdownKey} .active {
      background: ${accent10};
      color: ${general.accent.color1};
    }`,
    );

    this.stylesCleanup.update(
      this.elementInlineEditKey,
      `
      .${this.elementInlineEditKey} be-base-editor {
        --ie-color: ${this.cellColor};
        --ie-background: ${this.cellBackground};
        --ie-accent: ${general.accent.color1};
      }
      `,
    );
  }

  //#region table row click action

  private async executeRowClickAction(action: ContainerActionConfig, item: unknown): Promise<void> {
    await this.onAction({
      event,
      action: { action, label: '', id: ITEMS_CONTAINER_ACTION_ROW_CLICK },
      dataRow: item as DatasetRow,
      isBulkAction: false,
    });
    this.selection.clear();
    this.focus.clear();
    this.elementDataActions.toggleSelection(item, this.value, { allowBulkOutputEmit: true });
    this.cdr.markForCheck();
  }

  private assignRowActiveRowClickAction(): void {
    const content = this.props?.content;
    if (!content) {
      return;
    }

    if (this.assignRowActiveRowClickActionSub) {
      this.assignRowActiveRowClickActionSub.unsubscribe();
      this.assignRowActiveRowClickActionSub = undefined;
    }

    this.assignRowActiveRowClickActionSub = this.userAccessRolesService.userAccessRoles$
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        const accessRoles = content.rowClick?.visibilityConditions?.accessRoles;
        const isInAccessRoles =
          !accessRoles?.length ||
          this.userAccessRolesService.isCurrentUserInAccessRoles(accessRoles);

        if (content.rowClick?.enabled && isInAccessRoles) {
          const { action, targetId } = content.rowClick;
          this.actionOnRowClickId = action;
          this.actionOnRowClick =
            targetId && action && action in targetId
              ? (targetId?.[action] as ElementRedirect | string)
              : undefined;
        } else {
          this.actionOnRowClickId = undefined;
          this.actionOnRowClick = undefined;
        }
      });
  }

  //#endregion

  //#region table-inline-edit

  private updateLinkColumnsClickActions(): void {
    if (
      !this.props?.content.columns?.find((col) => col?.onLinkClick?.enabled && col.onLinkClick.page)
    ) {
      this.linkCellValueClickConfig = undefined;
      return;
    }
    this.linkCellValueClickConfig = {};
    if (!this.dataSource) {
      return;
    }
    this.props.content.columns?.forEach((column) => {
      if (!this.linkCellValueClickConfig) {
        return;
      }
      this.linkCellValueClickConfig[getColumnKey(column)] =
        (column.onLinkClick?.enabled && column?.onLinkClick?.page) || false;
    });
  }

  private updateColumnsToEdit(): void {
    if (!this.props?.content.table?.editable) {
      this.cellsEditConfig = undefined;
      return;
    }
    this.cellsEditConfig = {};
    this.isMultipleValueAllowed = {};
    const activeBindingDataSource = getActiveBinding(this.props.data)?.dataSource;
    if (!this.dataSource) {
      return;
    }
    this.props.content.columns?.forEach((column) => {
      if (!this.cellsEditConfig) {
        return;
      }
      const col = findColumn(activeBindingDataSource?.columns, column);

      this.cellsEditConfig[column.id ?? column.name] =
        (column.editable && getEditMode(col)) || false;

      if (!this.isMultipleValueAllowed) {
        return;
      }
      this.isMultipleValueAllowed[column.id ?? column.name] = !!(
        column.editable && col?.multipleValues
      );
    });
  }

  private getColumnKeyForEditableCheck(columnName: string): string | undefined {
    if (!this.props?.content.table?.editable) {
      return;
    }
    const activeBindingDataSource = getActiveBinding(this.props.data)?.dataSource;
    if (!this.dataSource) {
      return;
    }
    const col = this.props.content.columns?.find((column) => {
      const col = findColumn(activeBindingDataSource?.columns, column);
      return (column.name ?? column.label ?? col?.name) === columnName;
    });
    return getColumnKey(col);
  }

  private initializeOffsetsObservables(): void {
    this.headerPositionOffset$ = combineLatest({
      ctx: this.elementContext$,
      height: this.navHeaderHeight$,
    }).pipe(
      map(({ ctx, height }) => (ctx?.stickySlots?.header ? height : 0)),
      distinctUntilChanged(),
    );
    this.headerStartOffset$ = combineLatest({
      ctx: this.elementContext$,
      height: this.navHeaderHeight$,
    }).pipe(
      map(({ ctx, height }) => (ctx?.stickySlots?.header ? -1 * height : 0)),
      distinctUntilChanged(),
    );
    this.bottombarPositionOffset$ = combineLatest({
      ctx: this.elementContext$,
      height: this.navBottombarHeight$,
    }).pipe(
      map(({ ctx, height }) => (ctx?.stickySlots?.bottom ? height : 0)),
      distinctUntilChanged(),
    );
    this.bottombarEndOffset$ = combineLatest({
      ctx: this.elementContext$,
      height: this.navBottombarHeight$,
    }).pipe(
      map(({ ctx, height }) => (ctx?.stickySlots?.bottom ? -1 * height : 0)),
      distinctUntilChanged(),
    );
  }

  private initializeCellEditObservable(): void {
    this.cellEdit$$
      .pipe(
        filter(() => this.isInInlineEditMode),
        distinctUntilChanged(
          (prev, curr) =>
            prev?.columnName === curr?.columnName && prev?.rowNumber === curr?.rowNumber,
        ),
        untilDestroyed(this),
      )
      .subscribe(async (cellEditInfo) => {
        await scrollCellIntoViewIfNeeded(cellEditInfo, this.hostEl.nativeElement);
        this.findEditableElementForTabAction(cellEditInfo);

        if (!cellEditInfo) {
          return;
        }
        const columnSchema = this.dataValue.schema?.columns.find(
          (col) => col.name === cellEditInfo.columnName,
        );
        const schemaColumnId = this.dataValue.schema?.columns.find((col) => col.isId)?.name;

        if (!schemaColumnId) {
          return;
        }

        const overlayPositionBelow =
          (columnSchema?.dataType && MenuEditModeDataTypes.includes(columnSchema.dataType)) ||
          (columnSchema?.format?.formatType &&
            MenuEditModeColumnFormats.includes(columnSchema.format.formatType));

        const positionStrategy = generatePositionStrategyForOverlay(
          this.overlay,
          cellEditInfo,
          overlayPositionBelow,
        );

        const overlayRef = this.overlay.create({
          positionStrategy,
          scrollStrategy: this.overlay.scrollStrategies.reposition(),
          width: cellEditInfo.tableCell?.offsetWidth,
          height: cellEditInfo.tableCell?.offsetHeight,
          panelClass: [this.elementInlineEditKeyRoot, this.elementInlineEditKey],
        });

        const editorComponent = new ComponentPortal(TableBaseEditorComponent);
        const componentRef = overlayRef.attach(editorComponent);

        let value = this.dataSourceOrderedData[cellEditInfo.rowNumber][cellEditInfo.columnName];
        if (columnSchema?.dataType === 'GeoPoint') {
          value = isGeoPoint(value) ? formatGeoPoint(value) : '';
        }
        const savedErrorValue =
          this.cellsEditErrors[
            this.dataSourceOrderedData[cellEditInfo.rowNumber][schemaColumnId]
          ]?.[cellEditInfo.columnName]?.value;
        componentRef.instance.setInitialConfig(value, savedErrorValue, {
          textAlign: this.props?.content.columns?.find(
            (col) => col.name === cellEditInfo.columnName,
          )?.align,
          columnSchema,
          detailData: cellEditInfo.detailData,
          schema: this.dataValue.schema,
          columnLabel: this.displayedColumnsDef.find((col) =>
            cellEditInfo.columnId
              ? col.id === cellEditInfo.columnId
              : col.name === cellEditInfo.columnName,
          )?.label,
        });

        const focusLostObserver = merge(
          intersectionRxUtils
            .fromIntersectionObserver(cellEditInfo.tableCell, { threshold: 0.9 })
            .pipe(
              filter((value) => !value.some((i) => i.isIntersecting)),
              map(() => undefined),
            ),
          intersectionRxUtils
            .fromIntersectionObserver(cellEditInfo.tableCell, {
              threshold: 0.9,
              root: cellEditInfo.tableCell?.closest('table'),
            })
            .pipe(
              filter((value) => !value.some((i) => i.isIntersecting)),
              map(() => undefined),
            ),
          componentRef.instance.focusLost.pipe(
            tap(() => {
              if (this.cellMouseDown$$.value) {
                // this is to prevent a race between this & cell click event
                // (that enables editing in another cell)
                throw new TableCellExpectedDownError();
              }
            }),
            retry({
              count: 1,
              delay: () => {
                if (this.cellMouseDown$$.value) {
                  return this.cellMouseNotDown$;
                }
                return timer(0);
              },
            }),
          ),
          // trigger fix when component destroyed/route changed
          this.componentDestroy$.asObservable().pipe(map(() => undefined)),
        ).pipe(take(1), untilDestroyed(this));

        if (isColumnValueInlineEditable(columnSchema)) {
          focusLostObserver.subscribe((value) => {
            if (value !== undefined) {
              const schemaColumnIdName = this.dataValue.schema?.columns.find(
                (col) => col.isId,
              )?.name;
              if (!schemaColumnIdName) {
                return;
              }
              this.updateInlineRow(
                value,
                cellEditInfo.columnName,
                this.dataSourceOrderedData[cellEditInfo.rowNumber][schemaColumnIdName],
              );
            }
            destroyOverlayRef(overlayRef, cellEditInfo, this.cellEdit$$);
          });
        } else if (!!columnSchema?.dataType && ['File'].includes(columnSchema.dataType)) {
          focusLostObserver.subscribe(
            (val: FileResult | ({ selected: string[] } & CellValueUpdateModelForFileAction)) => {
              const schemaColumnIdName = this.dataValue.schema?.columns.find(
                (col) => col.isId,
              )?.name;
              if (schemaColumnIdName) {
                if (
                  (val as { selected: string[] } & CellValueUpdateModelForFileAction)?.isAddAction
                ) {
                  // adding new value
                  const incomingValue = (val as { selected: string[] | string }).selected;
                  if (Array.isArray(incomingValue)) {
                    this.updateInlineRow(
                      [...value, ...incomingValue],
                      cellEditInfo.columnName,
                      this.dataSourceOrderedData[cellEditInfo.rowNumber][schemaColumnIdName],
                    );
                  } else {
                    this.updateInlineRow(
                      incomingValue,
                      cellEditInfo.columnName,
                      this.dataSourceOrderedData[cellEditInfo.rowNumber][schemaColumnIdName],
                    );
                  }
                } else if ((val as CellValueUpdateModelForFileAction)?.isRemoveAction) {
                  // removing value
                  const incomingValue = val as FileResult;
                  let newValue: UpdateInlineRowValue = null;
                  if (Array.isArray(value)) {
                    newValue = value.filter((v) => v !== incomingValue.url);
                  } else {
                    newValue = null;
                  }
                  this.dialogService
                    .openConfirmDialog({
                      confirmLabel: 'Yes, remove',
                      cancelLabel: 'Cancel',
                      description:
                        'Are you sure you want to remove this file? This action cannot be undone.',
                      title: 'Delete File',
                    })
                    .afterClosed()
                    .subscribe((result) => {
                      if (result) {
                        this.updateInlineRow(
                          newValue,
                          cellEditInfo.columnName,
                          this.dataSourceOrderedData[cellEditInfo.rowNumber][schemaColumnIdName],
                        );
                      }
                    });
                }
              }
              destroyOverlayRef(overlayRef, cellEditInfo, this.cellEdit$$);
            },
          );
        } else if (!!columnSchema?.dataType && ['Image'].includes(columnSchema.dataType)) {
          focusLostObserver.subscribe(
            (val: FileResult | ({ selected: string } & CellValueUpdateModelForImageAction)) => {
              const schemaColumnIdName = this.dataValue.schema?.columns.find(
                (col) => col.isId,
              )?.name;
              if (schemaColumnIdName) {
                if (
                  (val as { selected: string[] | string } & CellValueUpdateModelForImageAction)
                    ?.isAddAction
                ) {
                  // adding new value
                  const incomingValue = (val as { selected: string[] | string }).selected;
                  if (Array.isArray(incomingValue)) {
                    this.updateInlineRow(
                      [...value, ...incomingValue],
                      cellEditInfo.columnName,
                      this.dataSourceOrderedData[cellEditInfo.rowNumber][schemaColumnIdName],
                    );
                  } else {
                    this.updateInlineRow(
                      incomingValue,
                      cellEditInfo.columnName,
                      this.dataSourceOrderedData[cellEditInfo.rowNumber][schemaColumnIdName],
                    );
                  }
                } else if ((val as CellValueUpdateModelForImageAction)?.isRemoveAction) {
                  this.dialogService
                    .openConfirmDialog({
                      confirmLabel: 'Yes, remove',
                      cancelLabel: 'Cancel',
                      description:
                        'Are you sure you want to remove this image? This action cannot be undone.',
                      title: 'Delete Image',
                    })
                    .afterClosed()
                    .subscribe((result) => {
                      if (result) {
                        const incomingValue = val as FileResult;
                        let newValue: UpdateInlineRowValue = null;
                        if (Array.isArray(value)) {
                          newValue = value.filter((v) => v !== incomingValue.url);
                        } else {
                          newValue = null;
                        }
                        this.updateInlineRow(
                          newValue,
                          cellEditInfo.columnName,
                          this.dataSourceOrderedData[cellEditInfo.rowNumber][schemaColumnIdName],
                        );
                      }
                    });
                }
              }
              destroyOverlayRef(overlayRef, cellEditInfo, this.cellEdit$$);
            },
          );
        } else if (!!columnSchema?.dataType && ['Tag'].includes(columnSchema.dataType)) {
          focusLostObserver.subscribe((val?: CellValueUpdateFromTag) => {
            if (val) {
              const schemaColumnIdName = this.dataValue.schema?.columns.find(
                (col) => col.isId,
              )?.name;
              if (schemaColumnIdName) {
                this.handleTagOrLinkActionInRow(
                  cellEditInfo.columnName,
                  cellEditInfo.rowNumber,
                  val.tag,
                  val.actionType,
                );
              }
            }
            destroyOverlayRef(overlayRef, cellEditInfo, this.cellEdit$$);
          });
        } else if (!!columnSchema?.dataType && ['Link'].includes(columnSchema.dataType)) {
          focusLostObserver.subscribe((val?: CellValueUpdateFromLink) => {
            if (val) {
              const schemaColumnIdName = this.dataValue.schema?.columns.find(
                (col) => col.isId,
              )?.name;
              if (schemaColumnIdName) {
                this.handleTagOrLinkActionInRow(
                  cellEditInfo.columnName,
                  cellEditInfo.rowNumber,
                  val.link.value,
                  val.actionType,
                );
              }
            }
            destroyOverlayRef(overlayRef, cellEditInfo, this.cellEdit$$);
          });
        } else if (
          !!columnSchema?.format?.formatType &&
          ['TimeZone'].includes(columnSchema.format.formatType)
        ) {
          focusLostObserver.subscribe((val?: CellValueUpdateFromTimeZone) => {
            if (val) {
              const schemaColumnIdName = this.dataValue.schema?.columns.find(
                (col) => col.isId,
              )?.name;
              if (schemaColumnIdName) {
                this.handleTagOrLinkActionInRow(
                  cellEditInfo.columnName,
                  cellEditInfo.rowNumber,
                  val.timezone?.value,
                  val.actionType,
                );
              }
            }
            destroyOverlayRef(overlayRef, cellEditInfo, this.cellEdit$$);
          });
        }

        resizeRxUtils
          .fromResizeObserver(cellEditInfo.tableCell ?? null)
          .pipe(takeUntil(focusLostObserver), untilDestroyed(this))
          .subscribe((entries) => {
            const { width, height } = entries[entries.length - 1].contentRect;
            overlayRef?.updateSize({ width, height });
            overlayRef?.updatePosition();
          });
      });
  }

  private findEditableElementForTabAction(cellEditInfo?: CellValueUpdateModel): void {
    if (!cellEditInfo) {
      this.editableElementsForTab = {
        next: undefined,
        prev: undefined,
      };
      return;
    }
    const editedColumnIndex = this.displayedColumnsDef.findIndex(
      (def) => def.name === cellEditInfo.columnName,
    );
    const nextFoundIndex = this.displayedColumnsDef
      .filter((_, i) => i >= editedColumnIndex)
      .findIndex(
        (def, id) =>
          id !== 0 &&
          this.cellsEditConfig?.[def.id || def.name] === 'inline-editor' &&
          def.visible &&
          def.editable,
      );
    let nextEditableColumnIndex = editedColumnIndex + nextFoundIndex;
    if (nextFoundIndex === -1) {
      nextEditableColumnIndex = this.displayedColumnsDef.findIndex(
        (def) =>
          this.cellsEditConfig?.[def.id || def.name] === 'inline-editor' &&
          def.visible &&
          def.editable,
      );
    }
    const nextEditableColumnName = this.displayedColumnsDef[nextEditableColumnIndex]?.name;

    let prevEditableColumnIndex = this.displayedColumnsDef
      .filter((_, i) => i < editedColumnIndex)
      .reverse()
      .findIndex(
        (def) =>
          this.cellsEditConfig?.[def.id || def.name] === 'inline-editor' &&
          def.visible &&
          def.editable,
      );
    if (prevEditableColumnIndex === -1) {
      prevEditableColumnIndex =
        this.displayedColumnsDef.length -
        deepClone(this.displayedColumnsDef)
          .reverse()
          .findIndex(
            (def) => this.cellsEditConfig?.[def.id || def.name] === 'inline-editor' && def.visible,
          ) -
        1;
    } else {
      prevEditableColumnIndex = editedColumnIndex - prevEditableColumnIndex - 1;
    }
    const prevEditableColumnName = this.displayedColumnsDef[prevEditableColumnIndex]?.name;

    const nextEditableColumnRow =
      cellEditInfo.rowNumber + (nextEditableColumnIndex > editedColumnIndex ? 0 : 1);
    const prevEditableColumnRow =
      cellEditInfo.rowNumber - (prevEditableColumnIndex < editedColumnIndex ? 0 : 1);

    const tableRowElement = cellEditInfo.tableCell?.closest('be-table-row');
    const nextEditableElementHTML =
      nextEditableColumnRow === cellEditInfo.rowNumber
        ? tableRowElement?.querySelector(`[data-col-name="${nextEditableColumnName}"]`)
        : tableRowElement?.nextElementSibling?.querySelector(
            `.cell[data-col-name="${nextEditableColumnName}"]`,
          );
    const prevEditableElementHTML =
      prevEditableColumnRow === cellEditInfo.rowNumber
        ? tableRowElement?.querySelector(`[data-col-name="${prevEditableColumnName}"]`)
        : tableRowElement?.previousElementSibling?.querySelector(
            `.cell[data-col-name="${prevEditableColumnName}"]`,
          );

    this.editableElementsForTab = {
      next: nextEditableColumnName
        ? ({
            columnName: nextEditableColumnName,
            rowNumber: nextEditableColumnRow,
            tableCell: nextEditableElementHTML,
          } as CellValueUpdateModel)
        : undefined,
      prev: prevEditableColumnName
        ? ({
            columnName: prevEditableColumnName,
            rowNumber: prevEditableColumnRow,
            tableCell: prevEditableElementHTML,
          } as CellValueUpdateModel)
        : undefined,
    };
  }

  private handleTagOrLinkActionInRow(
    columnName: string,
    rowNumber: number,
    value: string | BlazeTableRecord['value'],
    actionType: 'add' | 'create' | 'remove',
  ): void {
    const oldValue = this.dataSourceOrderedData[rowNumber][columnName];
    const schemaColumnId = this.dataValue.schema?.columns.find((col) => col.isId)?.name;
    if (!schemaColumnId) {
      return;
    }
    let newValue;
    if (Array.isArray(oldValue)) {
      switch (actionType) {
        case 'add':
          newValue = [...oldValue, value];
          break;
        case 'create':
          newValue = [...oldValue, value];
          break;
        case 'remove':
          if (typeof oldValue[0] === 'object') {
            newValue = oldValue.filter((v) => (v.display ?? v.value) !== value);
          } else {
            newValue = oldValue.filter((v) => v !== value);
          }
          break;
        default:
          newValue = oldValue;
      }
    } else {
      switch (actionType) {
        case 'add':
          newValue = value;
          break;
        case 'create':
          newValue = value;
          break;
        case 'remove':
          newValue = null;
          break;
        default:
          newValue = oldValue;
      }
    }
    this.updateInlineRow(
      newValue,
      columnName,
      this.dataSourceOrderedData[rowNumber][schemaColumnId],
    );
  }

  private updateInlineRow(value: UpdateInlineRowValue, columnName: string, id: string): void {
    const idColumnName = this.dataValue.schema?.columns.find(({ isId }) => isId)?.name;
    if (!idColumnName) {
      return;
    }

    const datasetId = isWorkflowSchema(this.dataValue.schema)
      ? this.dataValue.schema.tableId
      : undefined;
    const rowIndex = this.dataSourceOrderedData.findIndex((row) => row[idColumnName] === id);
    const dsValue = this.dataSourceOrderedData[rowIndex][columnName];

    if (!datasetId || (datasetId && idColumnName && dsValue === value)) {
      return;
    }
    const column = this.dataValue.schema?.columns.find((col) => col.name === columnName);

    if (column?.dataType === 'Duration' && isDurationEq(value as string, dsValue)) {
      return;
    }

    if (column?.dataType === 'GeoPoint' && isDeepEqual(dsValue, value)) {
      return;
    }

    if (
      ['Date', 'DateTime', 'Time'].includes(column?.dataType || '') &&
      isDateTimeEq(value as string, dsValue)
    ) {
      return;
    }

    if (column?.dataType === 'Number' && typeof value !== 'number' && dsValue === value) {
      return;
    }

    let updateTableAfterRequest = false;
    if (
      column?.columnType === 'Tag' &&
      !column.tagDefinition?.items.find((tag) => tag.value === value)
    ) {
      updateTableAfterRequest = true;
    }

    let oldValue: any;
    const newData = produce(this.dataSource.data, (draft) => {
      const index = this.dataSource.data.findIndex((row) => row[idColumnName] === id);
      if (index > -1) {
        oldValue = this.dataSource.data[index][columnName];
        draft[index][columnName] = value;
      }
    });
    this.dataSource.data = newData;
    safeDetectChanges(this.cdr);

    this.tableInlineEditorUtilsService
      .updateInlineRowValue(
        this.dataValue.schema,
        this.propsDataSource?.id,
        datasetId,
        value,
        columnName,
        id,
        idColumnName,
        this.dataSourceOrderedData[rowIndex],
      )
      .subscribe({
        next: (updatedRow) => {
          if (updatedRow && Object.keys(updatedRow).length) {
            const newData = produce(this.dataSource.data, (draft) => {
              const index = this.dataSource.data.findIndex((row) => row[idColumnName] === id);
              if (index > -1) {
                draft[index] = updatedRow;
              }
            });
            this.updateDataSet(newData, this.paginator);
            this.updateOutput(newData);
          }
          this.removeCellEditError(id, columnName);
          safeDetectChanges(this.cdr);
          if (updateTableAfterRequest) {
            this.schemaService.datasetIdsToRefresh$.next([datasetId]);
          }
        },
        error: ({ error }) => {
          this.addCellEditError(id, columnName, value, error.errorMessage);
          const newData = produce(this.dataSource.data, (draft) => {
            const index = this.dataSource.data.findIndex((row) => row[idColumnName] === id);
            if (index > -1) {
              draft[index][columnName] = oldValue;
            }
          });
          this.dataSource.data = newData;
          safeDetectChanges(this.cdr);
        },
      });
  }

  private addCellEditError(rowId: string, columnName: string, value: any, error: string): void {
    this.cellsEditErrors = produce(this.cellsEditErrors, (draft) => {
      draft[rowId] ??= {};
      (draft[rowId] as CellEditError)[columnName] = { value, error };
    });
  }

  private removeCellEditError(rowId: string, columnName: string): void {
    this.cellsEditErrors = produce(this.cellsEditErrors, (draft) => {
      const rowErrors = draft[rowId];
      if (rowErrors && rowErrors[columnName]) {
        rowErrors[columnName] = undefined;
      }
    });
  }

  //#endregion

  private loadProjectIdFromState(): void {
    this.store
      .select(runtimeSelectors.selectProjectId)
      .pipe(untilDestroyed(this))
      .subscribe((projectId) => {
        this.projectId = projectId;
        this.allowToSavePublicComplexFilters = this.authService.isCurrentUserProjectAdmin(
          this.projectId,
        );
      });
  }

  private loadSavedComplexFilters(): void {
    this.savedFiltersLoading = true;
    this.datasetsApiService.getDatasetFilters(this.elementId).subscribe((filters) => {
      this.savedFilters = filters;
      this.savedFiltersLoading = false;
      this.cdr.markForCheck();
    });
  }

  private shouldRefreshVirtualScroll(changes: SimpleChanges): {
    shouldResetItemCache: boolean;
    shouldUpdateContentSize: boolean;
  } {
    const currentLinePadding = changes['props']?.currentValue?.style?.table?.rowHeight;
    const previousLinePadding = changes['props']?.previousValue?.style?.table?.rowHeight;

    const currentRowLineClamp = changes['props']?.currentValue?.style?.table?.rowLineClamp;
    const previousLineClamp = changes['props']?.previousValue?.style?.table?.rowLineClamp;

    const currentStyle = changes['props']?.currentValue?.style;
    const previousStyle = changes['props']?.previousValue?.style;

    const styleChanged = !isDeepEqual(currentStyle, previousStyle);

    return {
      shouldResetItemCache: styleChanged,
      shouldUpdateContentSize:
        currentLinePadding !== previousLinePadding ||
        currentRowLineClamp !== previousLineClamp ||
        styleChanged,
    };
  }

  private assignGroupColsFormats(): void {
    this.elementDataActions.groupColsDefs =
      (this.props?.content.table?.groupBy
        .map((g) => g.field)
        .filter(Boolean)
        .map((colIdOrName) => findColumn(this.props?.content.columns, colIdOrName))
        .map((val) => ({
          ...val,
          name: GROUP_BY_KEY,
          originName: val?.name,
        })) as PropertiesConfigInputTableColumnModelForGrouping[]) || [];
  }

  private getColIdName(): string | undefined {
    return this.dataSource$$.value?.columns.find((col) => col.isId)?.name;
  }

  private updateConditionalInLineEdit(): void {
    const { content } = (this.props as TablePropsModel) || {};
    const conditionsCount =
      (content.table.editableConditions?.conditions?.length ?? 0) +
      (content.table.editableConditions?.accessRoles?.length ? 1 : 0);

    if (!content.table.editable || !conditionsCount) {
      // disable condition eval when inline editing disabled or no conditions set
      this.isInInlineEditMode = !content.table.editable ? false : !conditionsCount;

      if (this.isInInlineEditModeSub) {
        this.isInInlineEditModeSub.unsubscribe();
        this.isInInlineEditModeSub = undefined;
      }
      return;
    }

    if (!conditionsCount) {
      // always active inline editing
      this.isInInlineEditMode = true;
      return;
    }

    if (this.isInInlineEditModeSub) {
      return;
    }

    this.isInInlineEditModeSub = combineLatest({
      runtimeContext: this.runtimeContext$,
      userAccessRoles: this.userAccessRolesService.userAccessRoles$,
    })
      .pipe(
        map(({ runtimeContext }) => this.intelliSenseService.normalizeLinkedParams(runtimeContext)),
        untilDestroyed(this),
      )
      .subscribe((normalizedContext) => {
        const expr = this.props?.content?.table?.editableExpression;
        const accessRoles = content.table.editableConditions?.accessRoles;
        const isInAccessRoles = accessRoles?.length
          ? this.userAccessRolesService.isCurrentUserInAccessRoles(accessRoles)
          : true;
        this.isInInlineEditMode =
          isInAccessRoles &&
          this.intelliSenseService.evalExpressionObj(expr, normalizedContext, true);
      });
  }

  /**
   * Adds a temporary id to the @param data if it doesn't have one
   * This is required by groupBy feature to work properly
   * For example after complex workflow when we do not have `isId` column in schema
   */
  private addTempIdToData(data: any[]): any[] {
    const isIdCol = !!this.dataValue.schema?.columns.find((col) => col.isId);
    if (isIdCol) {
      return data;
    }
    // when groupBy is enabled
    const shouldAddRowId = this.props?.content.table?.groupBy.filter((val) => val.field).length;
    if (!shouldAddRowId) {
      return data;
    }

    return data.map((row) => {
      if (!row.__id) {
        return { ...row, __id: crypto.randomUUID() };
      }
      return row;
    });
  }

  private removeTempIdFromData(data: any): any {
    const isIdCol = !!this.dataValue.schema?.columns.find((col) => col.isId);
    if (isIdCol) {
      return data;
    }
    // when groupBy is enabled
    const shouldRemoveRowId = this.props?.content.table?.groupBy.filter((val) => val.field).length;
    if (shouldRemoveRowId) {
      const { __id, ...rest } = data;
      return rest;
    }
    return data;
  }
}

function addOrRemoveDisplayedColumnMutably(
  columnsNames: string[],
  columnName: string,
  shouldAdd: boolean,
  insertAtStart = false,
): void {
  const colIdx = columnsNames.indexOf(columnName);
  if (shouldAdd && colIdx === -1) {
    if (insertAtStart) {
      columnsNames.unshift(columnName);
    } else {
      columnsNames.push(columnName);
    }
  } else if (!shouldAdd && colIdx !== -1) {
    columnsNames.splice(colIdx, 1);
  }
}
